import { zodResolver } from "@hookform/resolvers/zod";
import { router, usePage } from "@inertiajs/react";
import { format } from "date-fns";
import {
	AlertCircle,
	Braces,
	Check,
	ChevronsUpDown,
	Copy,
	FileText,
	History,
	Info,
	Lightbulb,
	ListChecks,
	LoaderIcon,
	Lock,
	MapPin,
	TriangleAlert,
	UserRound,
} from "lucide-react";
import {
	Fragment,
	type ReactNode,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useState,
} from "react";
import { useForm, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { BypassCheckField } from "@/components/admin-portal/appointment/bypass-check-field";
import { isDocumentLocked } from "@/components/admin-portal/appointment/details/action-buttons";
import {
	holdsPicSlot,
	PIC_SLOT_KEYS,
	type PicSlotAdmin,
	PicSlotSelectors,
	type PicSlots,
} from "@/components/admin-portal/appointment/form/pic-slot-selectors";
import { INVOICE_EXPIRY_OPTIONS } from "@/components/admin-portal/appointment/settle-payments";
import {
	type AddressScopeResult,
	applyAddressToVisits,
	formatVisitNumbers,
	isSameAddress,
	type PutAddressHistory,
	pickPrefillVisit,
	sharesOneAddress,
	visitNoteKind,
	visitsToWarnAboutTherapist,
} from "@/components/admin-portal/appointment/update-address-scope";
import { useDateContext } from "@/components/providers/date-provider";
import {
	ADDRESS_ENTRY_SCHEMA,
	AddressEntryFields,
	type AddressEntryValues,
	parseCoordinate,
	useAddressEntryLabels,
} from "@/components/shared/address-entry-fields";
import { Disclosure } from "@/components/shared/disclosure";
import DotBadgeWithLabel from "@/components/shared/dot-badge";
import {
	ResponsiveDialogButton,
	useKeepDialogOpen,
} from "@/components/shared/responsive-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	MultiSelector,
	MultiSelectorContent,
	MultiSelectorInput,
	MultiSelectorItem,
	MultiSelectorList,
	MultiSelectorTrigger,
} from "@/components/ui/multi-select";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { getGenderIcon } from "@/hooks/use-gender";
import {
	buildSoapPayload,
	SOAP_CORE_FIELDS,
	SOAP_FINAL_FIELDS,
	SOAP_TEXT_LIMIT,
	type SoapFormValues,
	type SoapTextField,
	showsTherapyEvaluation,
	soapFormValues,
	visitCountOptions,
} from "@/lib/appointments/soap";
import { getDotVariantStatus } from "@/lib/appointments/utils";
import { PATIENT_CONDITIONS } from "@/lib/constants";
import { getCsrfToken } from "@/lib/csrf";
import i18n from "@/lib/i18n";
import {
	COMPANY_BANK_ACCOUNTS,
	destinationFromAccount,
	OTHER_BANK_ACCOUNT,
	PAYMENT_METHOD,
	prefillFromAccount,
} from "@/lib/orders/manual-payment";
import {
	estimateRepricedTotalDelta,
	leavesPaymentOpen,
	presetsFor,
	REBILL_PRESETS,
	RISKY_PRESETS,
	SETTLEMENT_PRESETS,
	type SettlementPreset,
} from "@/lib/orders/settlement-presets";
import {
	FALLBACK_STATUS_STYLE,
	ORDER_STATUS_STYLES,
	PAYMENT_STATUS_STYLES,
} from "@/lib/orders/status-styles";
import {
	buildVoucherFormData,
	estimateTotalDelta,
	isBelowMinOrder,
	isPackageIneligible,
	isRevalidateNoOp,
	type VoucherFormValues,
	type VoucherOption,
} from "@/lib/orders/voucher-change";
import { cn, formatCurrency, populateQueryParams } from "@/lib/utils";
import type { AppointmentIndexGlobalPageProps } from "@/pages/AdminPortal/Appointment/Index";
import type { OrdersGlobalPageProps } from "@/pages/AdminPortal/Appointment/Orders";
import type {
	Appointment,
	AppointmentSoap,
	AppointmentStatuses,
} from "@/types/admin-portal/appointment";
import type { Location } from "@/types/admin-portal/location";
import type { ResponsiveDialogMode } from "@/types/globals";
export function CancelAppointmentForm({
	selectedAppointment,
	forceMode,
}: {
	selectedAppointment: Appointment;
	forceMode?: ResponsiveDialogMode;
}) {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentIndexGlobalPageProps>();
	const [isLoading, setIsLoading] = useState(false);

	const buttonProps = useMemo<ResponsiveDialogButton>(() => {
		return {
			isLoading,
			forceMode,
			submitText: i18n.t("appointments:modal.cancel.button_submit"),
		};
	}, [isLoading, forceMode]);

	// The paid-order restriction only exists upstream, so the toggle is only shown
	// on the path that reaches it — an order-less appointment cancels locally.
	const hasOrder = !!selectedAppointment.order?.id;

	const formSchema = z.object({
		id: z.string(),
		orderId: z.string(),
		reason: z
			.string()
			.min(10, "Cancellation reason must be at least 10 characters"),
		force: z.boolean(),
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			id: selectedAppointment.id,
			orderId: selectedAppointment.order?.id || "",
			reason: "",
			force: false,
		},
		mode: "all",
	});

	const onSubmit = (values: z.infer<typeof formSchema>) => {
		// Use different endpoints based on whether order exists
		const routeURL = hasOrder
			? `${globalProps.adminPortal.router.adminPortal.appointment.index}/${values.orderId}/cancel-external`
			: `${globalProps.adminPortal.router.adminPortal.appointment.index}/${values.id}/cancel`;

		// populate current query params
		const { queryParams } = populateQueryParams(pageURL);
		// generate the submit form url with the source query params
		const { fullUrl } = populateQueryParams(routeURL, queryParams);
		const formData = deepTransformKeysToSnakeCase({
			formData: {
				id: values.id,
				reason: values.reason,
				force: values.force,
			},
		});
		router.put(fullUrl, formData, {
			preserveScroll: true,
			preserveState: true,
			onStart: () => {
				setIsLoading(true);
			},
			onFinish: () => {
				setTimeout(() => {
					setIsLoading(false);
				}, 250);
			},
		});
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				<FormField
					control={form.control}
					name="reason"
					render={({ field }) => (
						<FormItem>
							{/* <FormLabel>Cancellation Reason</FormLabel> */}
							<FormControl>
								<Textarea
									{...field}
									placeholder="Enter the cancellation reason (minimum 10 characters)..."
									className="shadow-inner bg-sidebar"
								/>
							</FormControl>

							<FormMessage />
						</FormItem>
					)}
				/>

				{hasOrder && (
					<FormField
						control={form.control}
						name="force"
						render={({ field }) => (
							<FormItem className="mt-3">
								<BypassCheckField
									id="force-cancel"
									keyPrefix="force_cancel"
									variant="between"
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}

				<ResponsiveDialogButton {...buttonProps} className="mb-1" />
			</form>
		</Form>
	);
}

export function UpdatePICForm({
	selectedAppointment,
	forceMode,
}: {
	selectedAppointment: Appointment;
	forceMode?: ResponsiveDialogMode;
}) {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentIndexGlobalPageProps>();
	const { t } = useTranslation("appointments");
	// The KPI slots of the Draft behind this Order, when there is one and DRAFT_PIC_KPI is
	// on. Absent for an appointment made before Drafts existed or synced from the
	// spreadsheet — there is then nowhere to record L1/L2/L3, so the flat list stands.
	const draftPics = selectedAppointment.draftPics;
	const admins = useMemo(
		() => globalProps.optionsData?.admins || [],
		[globalProps.optionsData?.admins],
	);
	// Every admin this dialog may have to *name*, which is more than it may hand a new
	// assignment to: the options list is screened by Admin.assignable, so somebody suspended
	// after being assigned is missing from it — and it is the only place the slot trigger and
	// the chips look a name up. Without the incumbents merged in, their slot reads "not
	// chosen yet" and their chip reads a raw uuid while the value behind both is set.
	// First entry wins, so the picker's own fuller record beats the summary.
	const adminDirectory = useMemo<PicSlotAdmin[]>(() => {
		const byId = new Map<string, PicSlotAdmin>();
		const candidates: PicSlotAdmin[] = [
			...admins.map((admin) => ({
				id: String(admin.id),
				name: admin.name,
				email: admin.user?.email,
			})),
			...(selectedAppointment.admins ?? []).map((admin) => ({
				id: String(admin.id),
				name: admin.name,
				email: admin.user?.email,
			})),
			...[
				draftPics?.picL1,
				draftPics?.picL2,
				draftPics?.picL3,
				...(draftPics?.supporting ?? []),
			].flatMap((admin) => (admin ? [admin] : [])),
		];

		for (const admin of candidates) {
			if (!byId.has(admin.id)) byId.set(admin.id, admin);
		}

		return [...byId.values()];
	}, [admins, selectedAppointment.admins, draftPics]);
	const [isLoading, setIsLoading] = useState(false);
	const buttonProps = useMemo<ResponsiveDialogButton>(() => {
		return {
			isLoading,
			forceMode,
		};
	}, [isLoading, forceMode]);
	const formSchema = z.object({
		id: z.string(),
		picL1: z.string().nullable(),
		picL2: z.string().nullable(),
		picL3: z.string().nullable(),
		// Ids, not names: two admins can share a name, and the old form resolved its
		// selection by matching on one — which mapped both of them onto the save.
		adminIds: z.array(z.string()),
	});
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			id: selectedAppointment.id,
			picL1: draftPics?.picL1?.id ? String(draftPics.picL1.id) : null,
			picL2: draftPics?.picL2?.id ? String(draftPics.picL2.id) : null,
			picL3: draftPics?.picL3?.id ? String(draftPics.picL3.id) : null,
			// The union of what the Draft calls supporting and who is actually on the
			// appointment, minus the slot holders. Those two can differ: until this dialog
			// existed, the only way to add a PIC here wrote appointment_admins and never the
			// Draft. Seeding from the Draft alone would hide those people and then delete
			// them from every visit on the next save, without ever showing their name.
			adminIds: [
				...new Set(
					[
						...(draftPics?.supporting ?? []),
						...(selectedAppointment?.admins ?? []),
					].map((admin) => String(admin.id)),
				),
			].filter(
				(id) =>
					!draftPics ||
					!holdsPicSlot(
						[draftPics.picL1?.id, draftPics.picL2?.id, draftPics.picL3?.id],
						id,
					),
			),
		},
		mode: "onSubmit",
	});
	const [validationAlert, setValidationAlert] = useState("");
	const onSubmit = (values: z.infer<typeof formSchema>) => {
		if (draftPics ? !values.picL1 : !values.adminIds?.length) {
			setValidationAlert(
				draftPics
					? t("modal.update_pic.needs_l1", {
							defaultValue: "Isi L1 dulu sebelum menyimpan.",
						})
					: "At least input one admin for PIC(s)",
			);
			return;
		}

		const routeURL = `${globalProps.adminPortal.router.adminPortal.appointment.index}/${values.id}/update-pic`;
		// populate current query params
		const { queryParams } = populateQueryParams(pageURL);
		// generate the submit form url with the source query params
		const { fullUrl } = populateQueryParams(routeURL, queryParams);
		// Written in the shape the server reads rather than run through
		// deepTransformKeysToSnakeCase, which spells picL1 as pic_l_1.
		const formData = {
			form_data: {
				id: values.id,
				// Always sent, empty included: a missing admin_ids tells the server to leave
				// the supporting PICs alone, which would make clearing them impossible.
				admin_ids: values.adminIds,
				...(draftPics
					? {
							pic_l1: values.picL1,
							pic_l2: values.picL2,
							pic_l3: values.picL3,
						}
					: {}),
			},
		};
		router.put(fullUrl, formData, {
			preserveScroll: true,
			preserveState: true,
			onStart: () => {
				setIsLoading(true);
			},
			onFinish: () => {
				setTimeout(() => {
					setIsLoading(false);
				}, 250);
			},
		});
	};

	const slotIds = useWatch({
		control: form.control,
		name: PIC_SLOT_KEYS,
	});
	const slots = useMemo<PicSlots>(
		() => ({ picL1: slotIds[0], picL2: slotIds[1], picL3: slotIds[2] }),
		[slotIds],
	);

	return (
		<div className="p-0.5 min-h-fit">
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
					{!!validationAlert && (
						<Alert variant="destructive">
							<AlertCircle className="w-4 h-4" />
							<AlertTitle>Error</AlertTitle>
							<AlertDescription>{validationAlert}</AlertDescription>
						</Alert>
					)}

					{!!draftPics && (
						<PicSlotSelectors
							className="md:grid-cols-1"
							admins={adminDirectory}
							value={slots}
							onChange={(next) => {
								for (const key of PIC_SLOT_KEYS) {
									form.setValue(key, next[key]);
								}
								// Somebody promoted into a slot leaves the supporting list — the two
								// are one union on the server, so one person must not read as two.
								form.setValue(
									"adminIds",
									form.getValues("adminIds").filter(
										(id) =>
											!holdsPicSlot(
												PIC_SLOT_KEYS.map((key) => next[key]),
												id,
											),
									),
								);
							}}
						/>
					)}

					<FormField
						control={form.control}
						name="adminIds"
						render={({ field }) => (
							<FormItem className={cn(!draftPics && "mb-0 md:mb-16")}>
								{!!draftPics && (
									<FormLabel className="uppercase">
										{t("modal.update_pic.supporting_label", {
											defaultValue: "PIC pendukung",
										})}
									</FormLabel>
								)}

								<FormControl>
									<MultiSelector
										onValuesChange={field.onChange}
										values={field?.value || []}
										maxShows={5}
										// The values are admin ids; without this the chips would spell
										// raw uuids at somebody who cannot check them. Read from the
										// directory, not the options, so an incumbent the suspension
										// screen hides still has a readable chip.
										labelFor={(id) =>
											adminDirectory.find((admin) => admin.id === id)?.name ??
											id
										}
									>
										<MultiSelectorTrigger>
											<MultiSelectorInput
												placeholder={`${i18n.t(
													"appointments:fields.admin_pic.placeholder",
												)}...`}
											/>
										</MultiSelectorTrigger>
										<MultiSelectorContent>
											{/* Options come from the picker list rather than the
											    directory: an incumbent must stay readable, but a
											    suspended account must not be handed a new assignment. */}
											<MultiSelectorList className="max-h-32">
												{admins
													.filter((admin) => !holdsPicSlot(slotIds, admin.id))
													.map((admin) => (
														<MultiSelectorItem
															key={admin.id}
															value={String(admin.id)}
														>
															<div className="min-w-0">
																<p className="truncate">{admin.name}</p>
																{!!admin.user?.email && (
																	<p className="text-xs truncate text-muted-foreground">
																		{admin.user.email}
																	</p>
																)}
															</div>
														</MultiSelectorItem>
													))}
											</MultiSelectorList>
										</MultiSelectorContent>
									</MultiSelector>
								</FormControl>

								{!!draftPics && (
									<p className="text-xs text-muted-foreground">
										{t("modal.update_pic.supporting_hint", {
											defaultValue:
												"Selain L1/L2/L3 di atas. Perubahan berlaku untuk semua kunjungan di order ini.",
										})}
									</p>
								)}
							</FormItem>
						)}
					/>

					<ResponsiveDialogButton {...buttonProps} />
				</form>
			</Form>
		</div>
	);
}

// Statuses hidden from manual selection: cascading ones (cancelled/on_hold
// cascade to sibling visits) and ones the admin flow never assigns by hand.
// Shared by UpdateStatusForm and BulkUpdateStatusForm so the two pickers
// can't drift apart.
const MANUAL_STATUS_EXCLUDED_KEYS = new Set([
	"on_hold",
	"unscheduled",
	"cancelled",
	"pending_therapist_assignment",
	"pending_patient_approval",
	"pending_mitra_approval",
]);

// The status combobox itself, shared by UpdateStatusForm (one appointment) and
// BulkUpdateStatusForm (many) — same Popover/Command markup, only what's
// selectable and how the current value resolves to a label differ per caller.
function AppointmentStatusPicker({
	value,
	onChange,
	options,
	isLoading,
	disabledKey,
	placeholder,
	searchPlaceholder,
	emptyText,
	getLabel,
}: {
	value: string | undefined;
	onChange: (key: string) => void;
	options: { key: string; value: string }[];
	isLoading: boolean;
	disabledKey?: string;
	placeholder: string;
	searchPlaceholder: string;
	emptyText: string;
	getLabel?: (key: string) => string;
}) {
	const label = value
		? (getLabel?.(value) ?? options.find((s) => s.key === value)?.value ?? "")
		: placeholder;

	return (
		<Popover modal>
			<PopoverTrigger asChild>
				<FormControl>
					<Button
						variant="outline"
						className={cn(
							"w-full justify-between font-normal shadow-inner bg-sidebar",
							!value && "text-muted-foreground",
						)}
						disabled={isLoading}
					>
						<p>{label}</p>

						<ChevronsUpDown className="opacity-50" />
					</Button>
				</FormControl>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				side="bottom"
				className="w-[var(--radix-popover-trigger-width)] p-0 "
			>
				<Command>
					<CommandInput
						disabled={isLoading}
						placeholder={searchPlaceholder}
						className="h-9"
					/>
					<CommandList>
						<CommandEmpty>{emptyText}</CommandEmpty>
						<CommandGroup>
							{isLoading ? (
								<CommandItem value={undefined} disabled>
									<LoaderIcon className="animate-spin" />
									<span>{`${i18n.t("components.modal.wait")}...`}</span>
								</CommandItem>
							) : (
								options.map((status) => (
									<CommandItem
										value={status.value}
										key={status.key}
										disabled={disabledKey === status.key}
										onSelect={() => onChange(status.key)}
									>
										{status.value}
										<Check
											className={cn(
												"ml-auto",
												status.key === value ? "opacity-100" : "opacity-0",
											)}
										/>
									</CommandItem>
								))
							)}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}

export function UpdateStatusForm({
	selectedAppointment,
	forceMode,
}: {
	selectedAppointment: Appointment;
	forceMode?: ResponsiveDialogMode;
}) {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentIndexGlobalPageProps>();
	const { t } = useTranslation("appointments");
	const [isLoading, setIsLoading] = useState(false);
	const buttonProps = useMemo<ResponsiveDialogButton>(
		() => ({
			isLoading,
			forceMode,
			submitText: t("modal.update_status.button_submit"),
		}),
		[isLoading, forceMode, t],
	);
	const appointmentStatuses = useMemo(() => {
		const { optionsData } = globalProps;

		if (!optionsData?.statuses) {
			console.log("No optionsData.statuses available");
			return [];
		}

		const filteredStatuses = optionsData.statuses.filter(
			(status) => !MANUAL_STATUS_EXCLUDED_KEYS.has(status.key),
		);

		return filteredStatuses;
	}, [globalProps.optionsData?.statuses, globalProps]);
	const formSchema = z.object({
		id: z.string(),
		status: z.enum(
			appointmentStatuses.map((status) => status.key) as [
				keyof typeof AppointmentStatuses,
				...(keyof typeof AppointmentStatuses)[],
			],
		),
		reason: z.string().optional(),
	});
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			id: selectedAppointment.id,
			status: selectedAppointment?.status,
			reason: "",
		},
		mode: "onSubmit",
	});
	const onSubmit = (values: z.infer<typeof formSchema>) => {
		const routeURL = `${globalProps.adminPortal.router.adminPortal.appointment.index}/${values.id}/update-status`;
		// populate current query params
		const { queryParams } = populateQueryParams(pageURL);
		// generate the submit form url with the source query params
		const { fullUrl } = populateQueryParams(routeURL, queryParams);
		const formData = deepTransformKeysToSnakeCase({ formData: { ...values } });
		router.put(fullUrl, formData, {
			preserveScroll: true,
			preserveState: true,
			onStart: () => {
				setIsLoading(true);
			},
			onFinish: () => {
				setTimeout(() => {
					setIsLoading(false);
				}, 250);
			},
		});
	};

	return (
		<div className="p-0.5 min-h-fit">
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
					<FormField
						control={form.control}
						name="status"
						render={({ field }) => (
							<FormItem className="flex flex-col">
								{/* <FormLabel>{t("fields.appointment_status.label")}</FormLabel> */}
								<AppointmentStatusPicker
									value={field.value}
									onChange={field.onChange}
									options={appointmentStatuses}
									isLoading={isLoading}
									disabledKey={selectedAppointment.status}
									placeholder={t("fields.appointment_status.placeholder")}
									searchPlaceholder={`${t("fields.appointment_status.search")}...`}
									emptyText={t("fields.appointment_status.empty_search")}
									getLabel={(key) =>
										appointmentStatuses.find((s) => s.key === key)?.value ??
										globalProps?.optionsData?.statuses?.find(
											(s) => s.key === key,
										)?.value ??
										""
									}
								/>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="reason"
						render={({ field }) => (
							<FormItem>
								{/* <FormLabel>Reason</FormLabel> */}
								<FormControl>
									<Textarea
										{...field}
										placeholder="Enter the reason..."
										className="shadow-inner bg-sidebar"
									/>
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<ResponsiveDialogButton {...buttonProps} />
				</form>
			</Form>
		</div>
	);
}

// * form of the bulk visit status update (Orders page, several visits at once)
export function BulkUpdateStatusForm({
	orderId,
	appointmentIds,
	onSuccess,
	forceMode,
}: {
	orderId: string;
	appointmentIds: string[];
	onSuccess?: () => void;
	forceMode?: ResponsiveDialogMode;
}) {
	const { props: globalProps, url: pageURL } = usePage<OrdersGlobalPageProps>();
	const { t } = useTranslation("appointments");
	const [isLoading, setIsLoading] = useState(false);
	const buttonProps = useMemo<ResponsiveDialogButton>(
		() => ({
			isLoading,
			forceMode,
			submitText:
				appointmentIds.length === 1
					? "Update 1 Visit"
					: `Update ${appointmentIds.length} Visits`,
		}),
		[isLoading, forceMode, appointmentIds.length],
	);

	// The backend only populates `statuses` when an `update_status` (or
	// similar) query param is present (see
	// PreparationOrdersAppointmentService#fetch_options_data) — the signal
	// UpdateStatusForm's own opener already sends, since it opens through a
	// query-param reload. This dialog opens from plain local state, so it
	// sends that same signal itself, every time it opens: optionsData always
	// arrives as `{ statuses: [], ... }` rather than unset, so "is it already
	// loaded" can't be told apart from "loaded, but empty because nothing
	// asked for statuses yet". `preserveUrl` keeps it out of the address bar —
	// the bulk dialog isn't otherwise URL-driven.
	// biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount only
	useEffect(() => {
		if (appointmentIds[0]) {
			router.get(
				pageURL,
				{ update_status: appointmentIds[0] },
				{
					only: ["optionsData"],
					preserveScroll: true,
					preserveState: true,
					preserveUrl: true,
				},
			);
		}
	}, []);

	const statusOptions = useMemo(() => {
		const { optionsData } = globalProps;

		if (!optionsData?.statuses) return [];

		return optionsData.statuses.filter(
			(status) => !MANUAL_STATUS_EXCLUDED_KEYS.has(status.key),
		);
	}, [globalProps.optionsData?.statuses, globalProps]);
	const formSchema = z.object({
		status: z.enum(
			statusOptions.map((status) => status.key) as [
				keyof typeof AppointmentStatuses,
				...(keyof typeof AppointmentStatuses)[],
			],
		),
		reason: z.string().optional(),
	});
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: { reason: "" },
		mode: "onSubmit",
	});
	const onSubmit = (values: z.infer<typeof formSchema>) => {
		const routeURL = `${globalProps.adminPortal.router.adminPortal.appointment.index}/orders/${orderId}/bulk-update-status`;
		// populate current query params
		const { queryParams } = populateQueryParams(pageURL);
		// generate the submit form url with the source query params
		const { fullUrl } = populateQueryParams(routeURL, queryParams);
		const formData = deepTransformKeysToSnakeCase({
			formData: { ...values, appointmentIds },
		});
		router.put(fullUrl, formData, {
			preserveScroll: true,
			preserveState: true,
			onStart: () => {
				setIsLoading(true);
			},
			onSuccess: () => {
				onSuccess?.();
			},
			onFinish: () => {
				setTimeout(() => {
					setIsLoading(false);
				}, 250);
			},
		});
	};

	return (
		<div className="p-0.5 min-h-fit">
			<p className="text-sm text-muted-foreground mb-4">
				Updating {appointmentIds.length} visit
				{appointmentIds.length === 1 ? "" : "s"}
			</p>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
					<FormField
						control={form.control}
						name="status"
						render={({ field }) => (
							<FormItem className="flex flex-col">
								<AppointmentStatusPicker
									value={field.value}
									onChange={field.onChange}
									options={statusOptions}
									isLoading={isLoading}
									placeholder={t("fields.appointment_status.placeholder")}
									searchPlaceholder={`${t("fields.appointment_status.search")}...`}
									emptyText={t("fields.appointment_status.empty_search")}
								/>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="reason"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Textarea
										{...field}
										placeholder="Enter the reason..."
										className="shadow-inner bg-sidebar"
									/>
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<ResponsiveDialogButton {...buttonProps} />
				</form>
			</Form>
		</div>
	);
}

// * form of the payment status update
interface UpdatePaymentFormProps {
	order: {
		id: string;
		registrationNumber: string;
		paymentStatus: string;
	};
	paymentStatusOptions: NonNullable<
		OrdersGlobalPageProps["optionsData"]
	>["paymentStatuses"];
	forceMode?: ResponsiveDialogMode;
	onSuccess?: () => void;
}

// Both are prefilled from the account above and stay editable.
const COPY_FIELDS = ["description", "notes"] as const;

export function UpdatePaymentForm({
	order,
	onSuccess,
	forceMode,
	paymentStatusOptions,
}: UpdatePaymentFormProps) {
	const { t } = useTranslation("appointments", { useSuspense: false });
	const [isLoading, setIsLoading] = useState(false);
	// Seeded from the Inertia error bag, not just from onError: the failure
	// redirect re-defers `selectedOrder`, which unmounts this dialog for a beat
	// and takes any local state with it. The props survive that; local state
	// then owns everything the admin does afterwards.
	const { errors: pageErrors } = usePage().props;
	const rejectedSettledPayment = pageErrors?.code === "settled_gateway_payment";
	const [error, setError] = useState<string | null>(
		pageErrors?.message ?? null,
	);
	// True once the backend rejects an UNPAID with 409: only then does the admin
	// see the override, and only after being told which receipt is blocking it.
	const [needsVoidOverride, setNeedsVoidOverride] = useState(
		rejectedSettledPayment,
	);
	const [voidConfirmed, setVoidConfirmed] = useState(false);

	const formSchema = useMemo(
		() =>
			z.object({
				payment_status: z.enum(
					paymentStatusOptions.map((option) => option.value) as [
						string,
						...string[],
					],
					{
						required_error: t("form.update_payment.status.required"),
					},
				),
				payment_method: z.string().optional(),
				bank_account: z.string(),
				bank_code: z.string().max(50).optional(),
				payment_destination: z.string().max(100).optional(),
				description: z.string().max(500).optional(),
				notes: z.string().max(500).optional(),
			}),
		[paymentStatusOptions, t],
	);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			// A rejected UNPAID puts the admin back where they were, so the override
			// they are being offered applies to the request they actually made.
			payment_status: rejectedSettledPayment ? "UNPAID" : order.paymentStatus,
			payment_method: PAYMENT_METHOD,
			bank_account: COMPANY_BANK_ACCOUNTS[0].accountNumber,
			bank_code: "",
			payment_destination: "",
			...prefillFromAccount(COMPANY_BANK_ACCOUNTS[0]),
		},
	});
	const [paymentStatus, bankAccount] = useWatch({
		control: form.control,
		name: ["payment_status", "bank_account"],
	});
	const isPaid = paymentStatus === "PAID";
	const isOtherBankAccount = bankAccount === OTHER_BANK_ACCOUNT;
	const buttonProps = useMemo<ResponsiveDialogButton>(
		() => ({
			isLoading,
			forceMode,
			disabled: needsVoidOverride && !voidConfirmed,
			submitText: needsVoidOverride
				? t("form.update_payment.void.submit")
				: t("form.update_payment.submit"),
		}),
		[isLoading, forceMode, needsVoidOverride, voidConfirmed, t],
	);

	const handleSubmit = async (values: z.infer<typeof formSchema>) => {
		setIsLoading(true);
		setError(null);

		// Where the money went only makes sense on a PAID, and those fields are
		// hidden otherwise — but their values survive in form state, so drop them
		// here too. Description and notes describe either direction, so they stay.
		const { payment_status, bank_account, description, notes, ...whereItWent } =
			values;
		// A listed account fills both halves itself; only "Other" uses what was typed.
		const destination = destinationFromAccount(bank_account);

		try {
			await router.put(
				`/admin-portal/appointments/orders/${order.id}/payment-status`,
				{
					form_data: {
						payment_status,
						description,
						notes,
						...(isPaid ? { ...whereItWent, ...destination } : {}),
						...(needsVoidOverride ? { force_void_settled_payment: true } : {}),
					},
				},
				{
					preserveState: true,
					preserveScroll: true,
					onSuccess: () => {
						onSuccess?.();
					},
					onError: (errors) => {
						const defaultMessage = t("form.update_payment.failed");
						const errorMessage = errors.message || defaultMessage;
						setError(errorMessage);
						// Re-derived from every response, never latched: a failure for some
						// other reason must not leave the override armed and pre-confirmed
						// for the next submit.
						setNeedsVoidOverride(errors.code === "settled_gateway_payment");
						setVoidConfirmed(false);
						console.error(defaultMessage, errorMessage);
					},
				},
			);
		} catch (err) {
			const defaultMessage = t("form.update_payment.unexpected");
			const errorMessage = err instanceof Error ? err.message : defaultMessage;
			setError(errorMessage);
			console.error(defaultMessage, errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="p-0.5 space-y-6">
			{error && (
				<Alert variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertTitle>{t("form.update_payment.error_title")}</AlertTitle>
					<AlertDescription className="space-y-3">
						<p>{error}</p>

						{needsVoidOverride && (
							<Label className="flex items-start gap-2 font-normal">
								<Checkbox
									checked={voidConfirmed}
									onCheckedChange={(checked) =>
										setVoidConfirmed(checked === true)
									}
								/>
								<span>{t("form.update_payment.void.confirm")}</span>
							</Label>
						)}
					</AlertDescription>
				</Alert>
			)}

			<Form {...form}>
				<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
					<FormField
						control={form.control}
						name="payment_status"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("form.update_payment.status.label")}</FormLabel>
								<Select
									onValueChange={(value) => {
										field.onChange(value);
										// A different target status is a different request; the
										// pending override and the error that prompted it no
										// longer describe what is about to be submitted.
										setError(null);
										setNeedsVoidOverride(false);
										setVoidConfirmed(false);
										// The default copy describes money coming in. On anything
										// else it would be a lie, so hand the fields back blank.
										const prefill = prefillFromAccount(
											value === "PAID"
												? COMPANY_BANK_ACCOUNTS.find(
														({ accountNumber }) =>
															accountNumber === form.getValues("bank_account"),
													)
												: undefined,
										);
										form.setValue("description", prefill.description);
										form.setValue("notes", prefill.notes);
									}}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger className="shadow-inner bg-sidebar">
											<SelectValue
												placeholder={t(
													"form.update_payment.status.placeholder",
												)}
											/>
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{paymentStatusOptions.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					{isPaid && (
						<Fragment>
							<FormField
								control={form.control}
								name="bank_account"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("form.update_payment.destination_account.label")}
										</FormLabel>
										<FormControl>
											<RadioGroup
												value={field.value}
												onValueChange={(value) => {
													field.onChange(value);
													const prefill = prefillFromAccount(
														COMPANY_BANK_ACCOUNTS.find(
															({ accountNumber }) => accountNumber === value,
														),
													);
													form.setValue("description", prefill.description);
													form.setValue("notes", prefill.notes);
												}}
												className="flex flex-col gap-2"
											>
												{COMPANY_BANK_ACCOUNTS.map(
													({ bankCode, accountNumber, accountName }) => (
														<div
															key={accountNumber}
															className="flex items-start p-3 space-x-3 border rounded-md bg-card shadow-inner"
														>
															<RadioGroupItem
																value={accountNumber}
																id={`bank-account-${accountNumber}`}
																className="mt-1"
															/>
															{/* min-w-0 keeps a long account name wrapping inside
															    the dialog instead of stretching it. */}
															<div className="min-w-0">
																<Label
																	htmlFor={`bank-account-${accountNumber}`}
																	className="font-normal cursor-pointer"
																>
																	{`${bankCode} ${accountNumber}`}
																</Label>
																<p className="text-sm break-words text-muted-foreground/80">
																	{t(
																		"form.update_payment.destination_account.account_name",
																		{
																			name: accountName,
																		},
																	)}
																</p>
															</div>
														</div>
													),
												)}

												<div className="flex items-start p-3 space-x-3 border rounded-md bg-card shadow-inner">
													<RadioGroupItem
														value={OTHER_BANK_ACCOUNT}
														id="bank-account-other"
														className="mt-1"
													/>
													<div className="min-w-0">
														<Label
															htmlFor="bank-account-other"
															className="font-normal cursor-pointer"
														>
															{t(
																"form.update_payment.destination_account.other.label",
															)}
														</Label>
														<p className="text-sm break-words text-muted-foreground/80">
															{t(
																"form.update_payment.destination_account.other.description",
															)}
														</p>
													</div>
												</div>
											</RadioGroup>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{isOtherBankAccount && (
								<Fragment>
									<FormField
										control={form.control}
										name="bank_code"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t("form.update_payment.bank_code.label")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															"form.update_payment.bank_code.placeholder",
														)}
														className="shadow-inner bg-sidebar"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="payment_destination"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t("form.update_payment.payment_destination.label")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														placeholder={t(
															"form.update_payment.payment_destination.placeholder",
														)}
														className="shadow-inner bg-sidebar"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</Fragment>
							)}
						</Fragment>
					)}

					{COPY_FIELDS.map((name) => (
						<FormField
							key={name}
							control={form.control}
							name={name}
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t(`form.update_payment.${name}.label`)}
									</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											placeholder={t(`form.update_payment.${name}.placeholder`)}
											className="shadow-inner bg-sidebar"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					))}

					<ResponsiveDialogButton {...buttonProps} />
				</form>
			</Form>
		</div>
	);
}

interface FeedbackReminderDialogProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	registrationNumber: string;
	orderId: string;
	onSuccess?: () => void;
}

export function FeedbackReminderDialog({
	isOpen,
	onOpenChange,
	registrationNumber,
	orderId,
	onSuccess,
}: FeedbackReminderDialogProps) {
	const [isSending, setIsSending] = useState(false);

	const handleSendReminder = useCallback(async () => {
		setIsSending(true);

		await router.post(
			`/admin-portal/appointments/orders/${orderId}/send-feedback-reminder`,
			{},
			{
				preserveState: true,
				preserveScroll: true,
				onSuccess: () => {
					toast.success("Feedback reminder email sent successfully");
					onOpenChange(false);
					onSuccess?.();
				},
				onError: (errors) => {
					const defaultMessage = "Failed to send feedback reminder";
					const errorMessage = errors.message || defaultMessage;
					console.error(defaultMessage, errorMessage);
					toast.error(errorMessage);
				},
				onFinish: () => {
					setIsSending(false);
				},
			},
		);
	}, [orderId, onOpenChange, onSuccess]);

	return (
		<AlertDialog open={isOpen} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Send Feedback Reminder?</AlertDialogTitle>
					<AlertDialogDescription>
						This will send a reminder email to the customer to provide feedback
						about this order ({registrationNumber}).
						<br />
						<br />
						Are you sure you want to proceed?
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={isSending}>Cancel</AlertDialogCancel>
					<AlertDialogAction
						onClick={(e) => {
							e.preventDefault();
							handleSendReminder();
						}}
						disabled={isSending}
					>
						{isSending ? "Sending..." : "Send Reminder"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

// Types for ChangePackageForm
export interface Package {
	id: number;
	name: string;
	numberOfVisit: number;
	totalPrice: number;
	currency: string;
	service: {
		id: number;
		name: string;
	};
}

interface ChangePackageFormProps {
	order: NonNullable<OrdersGlobalPageProps["selectedOrder"]>;
	packages: Package[];
	// `leavesPaymentOpen` is true when the change just made left a payment row an
	// admin can still act on, so the caller can offer those actions next.
	onSuccess?: (leavesPaymentOpen: boolean) => void;
	forceMode?: ResponsiveDialogMode;
}

// Minutes, matching the choices the booking form already offers for the same
// concept — an admin should not meet two different vocabularies for "how long
// does this payment link stay alive".
const EXPIRY_OPTIONS = ["30", "60", "180", "300", "720"] as const;
const DEFAULT_EXPIRY = "180";

// Only the list for the direction actually chosen is rendered; the old form let
// both hang open at once and neither got read.
const DOWNGRADE_RULES = [
	"completed_visits",
	"schedule_adjustment",
	"medical_records",
	"auto_sequence",
	"price_adjustment",
] as const;
const UPGRADE_RULES = [
	"additional_sessions",
	"therapist_assignment",
	"linked_history",
	"price_adjustment",
] as const;

// A hairline and a small caps label instead of another bordered card. Three
// stacked cards read as three equal things; this reads as one form with parts.
const FieldSetLabel = ({ children }: { children: ReactNode }) => (
	<p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
		{children}
	</p>
);

/* Hallmark · component: form notice · genre: modern-minimal
 * mood: utilitarian · tokens: project (shadcn/Tailwind, application.css)
 * fingerprint: small-caps label → sentence, hairline rule at the left edge
 * states: static (no interactive element)
 * pre-emit critique: P5 H5 E4 S5 R5 V4
 *
 * These notes used to be dashed boxes with an Info icon. Two of them can render one
 * under the other, and a box that repeats is a box nobody reads — the second one is
 * furniture by the time the eye arrives. The dialog's own fingerprint says hairline
 * dividers and stacked small-caps labels, no nested cards; the boxes were the one
 * place it broke its own rule.
 *
 * The label does the work the icon was pretending to do: it says which note this is
 * ("No refund" / "After saving"), so two stacked notes stop being interchangeable.
 */
const FormNotice = ({
	label,
	children,
}: {
	label: string;
	children: ReactNode;
}) => (
	<div className="border-l-2 pl-3 space-y-0.5">
		<p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
			{label}
		</p>
		<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
			{children}
		</p>
	</div>
);

// Same voice as FieldSetLabel, but a <dt> — the overview is a definition list,
// so the label has to be markup a screen reader pairs with its value.
export const LedgerLabel = ({ children }: { children: ReactNode }) => (
	<dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
		{children}
	</dt>
);

interface ChangePackageValues {
	new_package_id: string;
	preset: SettlementPreset;
	expiry_minutes?: string;
	reason?: string;
}

// What a downgrade would have to destroy to fit the smaller package. Shown before
// `force` is ever offered — an admin never chooses it blind.
interface ChangePackageConflict {
	visit_number: number;
	status: string;
	has_soap: boolean;
	has_evidence: boolean;
	reason: string;
}

interface PendingChangePackage {
	values: ChangePackageValues;
	overrides: Record<string, unknown>;
	// Which consequence the confirmation dialog spells out.
	kind: SettlementPreset | "force" | "reactivate";
}

/* Hallmark · component: change-package dialog form · genre: modern-minimal
 * mood: utilitarian · tokens: project (shadcn/Tailwind, application.css)
 * fingerprint: context line → decision + price delta → money → confirm;
 *              hairline dividers, stacked small-caps labels, no nested cards
 * states: default · hover · focus · active · disabled · loading · error · success
 * pre-emit critique: P5 H5 E4 S5 R4 V4
 */
export function ChangePackageForm({
	order,
	packages,
	onSuccess,
	forceMode,
}: ChangePackageFormProps) {
	const { t } = useTranslation("appointments", { useSuspense: false });

	const [isLoading, setIsLoading] = useState(false);
	// Seeded from the Inertia error bag rather than only from onError: the failure
	// redirect re-defers `selectedOrder`, which unmounts this dialog for a beat and
	// takes local state with it. See UpdatePaymentForm for the same pattern.
	const { errors: pageErrors } = usePage().props;
	// What the last attempt sent, echoed back by the controller. Everything the form
	// shows after a failure — including both retry buttons — is rebuilt from this.
	const submitted = pageErrors?.submitted as unknown as
		| Partial<ChangePackageValues>
		| undefined;
	const [selectedPackage, setSelectedPackage] = useState<Package | null>(
		packages.find((pkg) => pkg.id.toString() === submitted?.new_package_id) ??
			null,
	);
	const [error, setError] = useState<string | null>(
		pageErrors?.message ?? null,
	);
	// Both are situational overrides, never presets: they appear only once the
	// backend has refused, and only alongside what they would override.
	const [conflicts, setConflicts] = useState<ChangePackageConflict[]>(
		(pageErrors?.conflicts as unknown as ChangePackageConflict[]) ?? [],
	);
	const [canReactivate, setCanReactivate] = useState(
		pageErrors?.code === "order_cancelled",
	);
	// What the confirmation dialog is currently asking about: the values to send and
	// the raw override they carry, if any.
	const [pending, setPending] = useState<PendingChangePackage | null>(null);

	const currentPackage = order.package || null;

	const isDowngrade = useMemo(() => {
		if (!selectedPackage || !currentPackage) return false;
		return selectedPackage.numberOfVisit < currentPackage.numberOfVisit;
	}, [selectedPackage, currentPackage]);

	const isUpgrade = useMemo(() => {
		if (!selectedPackage || !currentPackage) return false;
		return selectedPackage.numberOfVisit > currentPackage.numberOfVisit;
	}, [selectedPackage, currentPackage]);

	// The list, split by what each package does to this order and ordered nearest
	// first inside each split. The backend already narrows it to the same service
	// and drops the current package, so every row here is a real swap — the only
	// thing missing was which way it goes.
	const packageGroups = useMemo(() => {
		const byDistance = (a: Package, b: Package) =>
			Math.abs(a.numberOfVisit - (currentPackage?.numberOfVisit ?? 0)) -
			Math.abs(b.numberOfVisit - (currentPackage?.numberOfVisit ?? 0));

		const of = (direction: "upgrade" | "downgrade" | "same") => ({
			direction,
			packages: packages
				.filter((pkg) => {
					const delta =
						pkg.numberOfVisit - (currentPackage?.numberOfVisit ?? 0);
					if (direction === "upgrade") return delta > 0;
					if (direction === "downgrade") return delta < 0;
					return delta === 0;
				})
				.sort(byDistance),
		});

		return [of("upgrade"), of("downgrade"), of("same")].filter(
			(group) => group.packages.length > 0,
		);
	}, [packages, currentPackage]);

	// Package list price on both sides. The order's final total is recomputed by
	// the backend once discount, voucher and tax are re-applied, so the strip
	// says "package price" and does not claim to be the new invoice total.
	const priceDelta =
		selectedPackage && currentPackage
			? selectedPackage.totalPrice - currentPackage.totalPrice
			: 0;

	// A downgrade is not automatically a refund: the backend only takes the refund path
	// once the order actually holds cash, and on one that has paid nothing it re-prices
	// the open payment instead.
	//
	// `priceDelta` is a LIST-price difference and the strip above says so; it must not be
	// handed to the settlement rule, which compares against cash and needs the total. On a
	// discounted order the two disagree by the whole discount, and in the direction that
	// invents refunds. Restated in total-space first.
	const totalDelta = selectedPackage
		? estimateRepricedTotalDelta({
				subtotalDelta: priceDelta,
				subtotal: order.subtotal,
				discountAmount: order.discountAmount,
				taxPercentage: order.taxPercentage,
				currentTotal: order.totalAmount,
			})
		: null;

	const presets = presetsFor({
		delta: totalDelta,
		settledCash: order.paidAmount,
		currentTotal: order.totalAmount,
	});

	// Said in the one case an admin would otherwise expect a refund and not find one.
	const isRebillOnly = presets === REBILL_PRESETS && (totalDelta ?? 0) < 0;

	const buttonProps = useMemo<ResponsiveDialogButton>(
		() => ({
			isLoading,
			forceMode,
			submitText: t("button.change_package.submit"),
		}),
		[isLoading, forceMode, t],
	);

	const formSchema = useMemo(
		() =>
			z
				.object({
					new_package_id: z.string().min(1, t("validation.package_required")),
					preset: z.enum(
						Object.keys(SETTLEMENT_PRESETS) as [
							SettlementPreset,
							...SettlementPreset[],
						],
					),
					expiry_minutes: z.string().optional(),
					reason: z.string().max(500).optional(),
				})
				.refine(
					(values) =>
						!RISKY_PRESETS.includes(values.preset) || !!values.reason?.trim(),
					{
						path: ["reason"],
						message: t("form.change_package.payment.reason.required"),
					},
				),
		[t],
	);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			new_package_id: submitted?.new_package_id ?? "",
			preset: submitted?.preset ?? "gateway",
			expiry_minutes: submitted?.expiry_minutes ?? DEFAULT_EXPIRY,
			reason: submitted?.reason ?? "",
		},
	});
	const preset = useWatch({ control: form.control, name: "preset" });
	const expiryMinutes = useWatch({
		control: form.control,
		name: "expiry_minutes",
	});

	// The request body, shown verbatim in the advanced disclosure. The settlement flags
	// are no longer in it: Rails derives them from `preset` and refuses to be told them,
	// so showing them here would be a receipt for a request nobody sends.
	const rawPayload = useMemo(
		() => ({
			preset,
			...(preset === "gateway" && expiryMinutes
				? { expiry_minutes: Number(expiryMinutes) }
				: {}),
		}),
		[preset, expiryMinutes],
	);

	// A preset only means something in one direction, so switching direction picks
	// that direction's default rather than carrying an inapplicable choice over.
	// No early return on "nothing picked yet": an overpaid order shows the refund list
	// before a package is chosen, and the form's default is a charge preset that is not on
	// it — which renders a radio group with nothing selected.
	useEffect(() => {
		if (!presets.includes(form.getValues("preset"))) {
			form.setValue("preset", presets[0]);
		}
	}, [presets, form]);

	const submitPayload = (values: ChangePackageValues, overrides = {}) => {
		setIsLoading(true);
		setError(null);

		// Overrides an admin ticked, plus the link lifetime. The settlement itself travels
		// as `preset` alone — Rails derives the flags from it.
		const extras: Record<string, unknown> = { ...overrides };
		if (values.preset === "gateway" && values.expiry_minutes) {
			extras.expiry_minutes = values.expiry_minutes;
		}

		router.put(
			`/admin-portal/appointments/orders/${order.id}/change-package`,
			{
				form_data: {
					new_package_id: values.new_package_id,
					reason: values.reason,
					// The settlement. Rails turns this into the flags the backend takes,
					// and echoes it back in the error bag so a form unmounted by the
					// failure can restore the admin's choice.
					preset: values.preset,
					...extras,
				},
			},
			{
				preserveState: true,
				preserveScroll: true,
				onFinish: () => {
					setIsLoading(false);
					setPending(null);
				},
				onSuccess: () => {
					// No price difference means no payment row was created at all, so
					// there is nothing to hand over however the preset was set.
					onSuccess?.(leavesPaymentOpen(values.preset) && priceDelta !== 0);
				},
				onError: (errors) => {
					const defaultMessage = t("form.change_package.failed");
					const errorMessage = errors.message || defaultMessage;
					setError(errorMessage);
					// Re-derived from every response, never latched: an unrelated failure
					// must not leave `force` or reactivation armed for the next attempt.
					setConflicts(
						(errors.conflicts as unknown as ChangePackageConflict[]) ?? [],
					);
					setCanReactivate(errors.code === "order_cancelled");
					console.error(defaultMessage, errorMessage);
				},
			},
		);
	};

	const handleSubmit = (values: ChangePackageValues) => {
		// Risky presets get a second yes that names the consequence; everything else
		// submits on the first one.
		if (RISKY_PRESETS.includes(values.preset)) {
			setPending({ values, overrides: {}, kind: values.preset });
			return;
		}

		submitPayload(values);
	};

	// Retry with `force`: the same payload plus permission to cascade-delete the
	// SOAP/Evidence listed above. Confirmed twice, and never without a reason.
	const retryWithForce = () => {
		const values = form.getValues();
		if (!values.reason?.trim()) {
			form.setError("reason", {
				message: t("form.change_package.payment.reason.required"),
			});
			return;
		}

		setPending({ values, overrides: { force: true }, kind: "force" });
	};

	// Retry on a cancelled order. Reactivation destroys nothing by itself, but it
	// puts an order somebody deliberately cancelled back into the schedule — so it
	// asks for a reason and a second yes, the same as the other overrides.
	const retryWithReactivation = () => {
		const values = form.getValues();
		if (!values.new_package_id) return;

		if (!values.reason?.trim()) {
			form.setError("reason", {
				message: t("form.change_package.payment.reason.required"),
			});
			return;
		}

		setPending({
			values,
			overrides: { reactivate_if_cancelled: true },
			kind: "reactivate",
		});
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
				{/* Who and where — registration number, patient, service, visit city —
				    then the three facts that decide the rest of this form: where the
				    package stands now, whether the order already holds money, and
				    whether its status allows a change at all. */}
				<div className="space-y-3">
					{/* Four bare values separated by commas read as one sentence the eye
					    has to parse; labelled, each becomes a field an admin can check
					    against whatever they have open next to this dialog. Two columns
					    at every width — a city like KOTA ADM. JAKARTA PUSAT has nowhere
					    to go in a quarter of a 560px dialog. */}
					<dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 px-1">
						<div className="min-w-0">
							<LedgerLabel>
								{t("form.change_package.overview.registration_number")}
							</LedgerLabel>
							<dd className="font-mono text-sm font-semibold">
								{order.registrationNumber || "—"}
							</dd>
						</div>

						<div className="min-w-0">
							<LedgerLabel>
								{t("form.change_package.overview.patient")}
							</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{order?.patient?.name || "—"}
							</dd>
						</div>

						<div className="min-w-0">
							<LedgerLabel>
								{t("form.change_package.overview.service")}
							</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{order?.appointments?.[0]?.serviceName?.replaceAll("_", " ") ||
									"—"}
							</dd>
						</div>

						<div className="min-w-0">
							<LedgerLabel>
								{t("form.change_package.overview.city")}
							</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{order?.appointments?.[0]?.locationCity || "—"}
							</dd>
						</div>
					</dl>

					<dl className="grid grid-cols-1 rounded-md border bg-muted/30 divide-y sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
						<div className="px-3 py-2 min-w-0">
							<LedgerLabel>
								{t("form.change_package.overview.current_package")}
							</LedgerLabel>
							<dd className="text-sm font-medium truncate">
								{currentPackage?.name || "—"}
							</dd>
							<dd className="text-xs text-muted-foreground">
								{currentPackage
									? `${currentPackage.numberOfVisit} ${t("common.visits")}`
									: "—"}
							</dd>
						</div>

						<div className="px-3 py-2 min-w-0">
							<LedgerLabel>
								{t("form.change_package.overview.paid")}
							</LedgerLabel>
							{/* Which settle-up branch the backend takes depends on whether
							    the order already holds cash, so the amount leads and the
							    total it is measured against sits under it. */}
							<dd className="text-sm font-medium tabular-nums">
								{formatCurrency(order.paidAmount)}
							</dd>
							<dd className="text-xs text-muted-foreground tabular-nums">
								{t("form.change_package.overview.of_total", {
									total: formatCurrency(order.totalAmount),
								})}
							</dd>
						</div>

						<div className="px-3 py-2 min-w-0 space-y-1">
							<LedgerLabel>
								{t("form.change_package.overview.status")}
							</LedgerLabel>
							<dd className="flex flex-wrap gap-1">
								<Badge
									className={cn(
										"text-[10px] border p-0 px-1 rounded-full font-bold",
										PAYMENT_STATUS_STYLES[order.paymentStatus] ||
											FALLBACK_STATUS_STYLE,
									)}
								>
									{order.paymentStatus.replace(/_/g, " ")}
								</Badge>
								<Badge
									className={cn(
										"text-[10px] border p-0 px-1 rounded-full font-bold",
										ORDER_STATUS_STYLES[order.status] || FALLBACK_STATUS_STYLE,
									)}
								>
									{order.status.replace(/_/g, " ")}
								</Badge>
							</dd>
						</div>
					</dl>
				</div>

				{error && (
					<div className="border border-destructive/40 rounded-md overflow-hidden">
						<div className="flex items-start gap-2 px-3 py-2.5 bg-destructive/5 text-destructive">
							<AlertCircle className="size-4 mt-0.5 shrink-0" />
							<p className="text-sm font-medium text-pretty">{error}</p>
						</div>

						{/* Out of the red text and onto its own surface: this is the list
						    the admin has to read before force is even thinkable. */}
						{conflicts.length > 0 && (
							<div className="px-3 py-3 space-y-3 border-t border-destructive/25">
								<p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
									{t("form.change_package.conflicts.header")}
								</p>
								<ul className="divide-y divide-border rounded-md border bg-card">
									{conflicts.map((conflict) => (
										<li
											key={conflict.visit_number}
											className="flex items-baseline gap-2 px-2.5 py-2 text-xs"
										>
											<span className="font-mono font-semibold shrink-0">
												#{conflict.visit_number}
											</span>
											<span className="min-w-0">
												<span className="font-medium">
													{conflict.status.replace(/_/g, " ")}
												</span>
												{(conflict.has_soap || conflict.has_evidence) && (
													<span className="text-muted-foreground">
														{conflict.has_soap &&
															` · ${t("form.change_package.conflicts.has_soap")}`}
														{conflict.has_evidence &&
															` · ${t("form.change_package.conflicts.has_evidence")}`}
													</span>
												)}
												<span className="block text-muted-foreground/80">
													{conflict.reason}
												</span>
											</span>
										</li>
									))}
								</ul>
								<Button
									type="button"
									size="sm"
									variant="destructive"
									className="w-full text-xs"
									onClick={retryWithForce}
								>
									{t("form.change_package.conflicts.retry_with_force")}
								</Button>
							</div>
						)}

						{canReactivate && (
							<div className="px-3 py-3 space-y-2 border-t border-destructive/25">
								<p className="text-xs text-muted-foreground text-pretty">
									{t("form.change_package.reactivate.description")}
								</p>
								<Button
									type="button"
									size="sm"
									variant="outline"
									className="w-full text-xs"
									disabled={isLoading}
									onClick={retryWithReactivation}
								>
									{t("form.change_package.reactivate.retry")}
								</Button>
							</div>
						)}
					</div>
				)}

				{/* Side by side from md up — the decision on the left, the money it
				    causes on the right — so the price delta stays on screen while the
				    payment preset is picked. One column below md, unchanged. */}
				<div className="grid gap-6 md:grid-cols-2 md:items-start">
					{/* ─── The decision ─────────────────────────────────────────────── */}
					<section className="space-y-3">
						<FieldSetLabel>{t("form.new_package.label")}</FieldSetLabel>

						<FormField
							control={form.control}
							name="new_package_id"
							render={({ field }) => (
								<FormItem>
									{/* `modal` because this popover opens inside a dialog. The dialog
								    hands its own subtree to react-remove-scroll, and the popover
								    portals to the body — outside it — so every wheel event over
								    the list was being cancelled as page scroll. A modal popover
								    brings its own scroll allowance. */}
									<Popover modal>
										<PopoverTrigger asChild>
											<FormControl>
												<Button
													variant="outline"
													className={cn(
														"w-full justify-between shadow-inner bg-sidebar",
														!field.value && "text-muted-foreground/75",
													)}
												>
													{field.value ? (
														<span className="font-medium tracking-tight truncate">
															{
																packages.find(
																	(pkg) => pkg.id.toString() === field.value,
																)?.name
															}{" "}
															<span className="text-muted-foreground/75 font-normal italic">
																&mdash;{" "}
																{
																	packages.find(
																		(pkg) => pkg.id.toString() === field.value,
																	)?.numberOfVisit
																}{" "}
																{t("common.visits")}
															</span>
														</span>
													) : (
														t("form.new_package.placeholder")
													)}
													<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-75" />
												</Button>
											</FormControl>
										</PopoverTrigger>
										<PopoverContent
											className="w-full p-0 min-w-[var(--radix-popover-trigger-width)]"
											align="start"
										>
											<Command>
												<CommandInput
													placeholder={t("form.new_package.search_placeholder")}
												/>
												<CommandList>
													<CommandEmpty>
														{t("form.new_package.no_package_found")}
													</CommandEmpty>

													{/* Grouped by what the swap does to the order, because that
												    is the question being answered — the old flat list made
												    an admin compare visit counts in their head to find out
												    whether they were about to bill or to refund. */}
													{packageGroups.map((group) => (
														<CommandGroup
															key={group.direction}
															heading={
																<span className="flex items-baseline justify-between gap-2">
																	<span>
																		{t(
																			`form.new_package.groups.${group.direction}`,
																		)}
																	</span>
																	<span className="tabular-nums font-normal opacity-60">
																		{group.packages.length}
																	</span>
																</span>
															}
														>
															{group.packages.map((pkg) => {
																const delta =
																	pkg.totalPrice -
																	(currentPackage?.totalPrice ?? 0);
																const visitDelta =
																	pkg.numberOfVisit -
																	(currentPackage?.numberOfVisit ?? 0);

																return (
																	<CommandItem
																		value={pkg.name}
																		key={pkg.id}
																		onSelect={(value) => {
																			const selected = packages.find(
																				(p) => p.name === value,
																			);
																			if (selected) {
																				form.setValue(
																					"new_package_id",
																					selected.id.toString(),
																				);
																				setSelectedPackage(selected);
																			}
																		}}
																		className="items-start gap-3"
																	>
																		<span className="min-w-0 flex-1 grid gap-0.5">
																			<span className="flex items-baseline justify-between gap-3">
																				<span className="font-medium tracking-tight truncate">
																					{pkg.name}
																				</span>
																				<span className="text-xs tabular-nums text-muted-foreground shrink-0">
																					{formatCurrency(pkg.totalPrice)}
																				</span>
																			</span>

																			{/* The two numbers that decide the rest of the
																		    form, said per row rather than only after the
																		    pick: how many visits it moves, and which way
																		    the money goes. */}
																			<span className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground tabular-nums">
																				<span>
																					{pkg.numberOfVisit}{" "}
																					{t("common.visits")}
																					{visitDelta !== 0 && (
																						<span className="opacity-75">
																							{" "}
																							({visitDelta > 0 ? "+" : "−"}
																							{Math.abs(visitDelta)})
																						</span>
																					)}
																				</span>
																				{delta !== 0 && (
																					<span
																						className={cn(
																							"font-medium shrink-0",
																							delta > 0
																								? "text-amber-700"
																								: "text-emerald-700",
																						)}
																					>
																						{delta > 0 ? "+" : "−"}
																						{formatCurrency(Math.abs(delta))}
																					</span>
																				)}
																			</span>
																		</span>

																		<Check
																			className={cn(
																				"mt-0.5 h-4 w-4 shrink-0",
																				pkg.id.toString() === field.value
																					? "opacity-100"
																					: "opacity-0",
																			)}
																		/>
																	</CommandItem>
																);
															})}
														</CommandGroup>
													))}
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* The number the old dialog never showed. Package list price on both
					    sides — the order's final total is recomputed by the backend once
					    discount, voucher and tax are re-applied, so it is not claimed here. */}
						{selectedPackage && currentPackage && (
							<div className="rounded-md border bg-muted/30 px-3 py-2.5 space-y-1.5">
								<div className="flex items-baseline justify-between gap-3 text-sm">
									<span className="min-w-0 truncate">
										<span className="text-muted-foreground">
											{currentPackage.name}
										</span>
										<span className="text-muted-foreground/60"> → </span>
										<span className="font-medium">{selectedPackage.name}</span>
									</span>
									<span
										className={cn(
											"font-semibold tabular-nums shrink-0",
											priceDelta > 0 && "text-amber-700",
											priceDelta < 0 && "text-emerald-700",
										)}
									>
										{priceDelta > 0 ? "+" : priceDelta < 0 ? "−" : ""}
										{formatCurrency(Math.abs(priceDelta))}
									</span>
								</div>

								<div className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground tabular-nums">
									<span>
										{currentPackage.numberOfVisit} →{" "}
										{selectedPackage.numberOfVisit} {t("common.visits")}
									</span>
									<span>
										{formatCurrency(currentPackage.totalPrice)} →{" "}
										{formatCurrency(selectedPackage.totalPrice)}
									</span>
								</div>

								<p className="text-[11px] text-muted-foreground/75 text-pretty">
									{t("form.change_package.delta.note")}
								</p>
							</div>
						)}

						{/* One disclosure, for the direction actually chosen. The old form
					    carried both rule lists open-able at once. */}
						{selectedPackage && (isDowngrade || isUpgrade) && (
							<Disclosure
								icon={ListChecks}
								title={
									isDowngrade
										? t("form.change_package.rules.downgrade.header")
										: t("form.change_package.rules.upgrade.header")
								}
								hint={t("form.change_package.rules.hint")}
							>
								{/* A term and what it means for this order — a definition list, the
							    same markup the overview ledger above uses. */}
								<dl className="divide-y divide-border/60">
									{(isDowngrade ? DOWNGRADE_RULES : UPGRADE_RULES).map(
										(key) => {
											const scope = isDowngrade ? "downgrade" : "upgrade";

											return (
												<div key={key} className="py-2 first:pt-0 last:pb-0">
													<dt className="text-xs font-medium leading-none">
														{t(
															`form.change_package.rules.${scope}.items.${key}.title`,
														)}
													</dt>
													<dd className="mt-1 text-[11px] leading-snug text-muted-foreground text-pretty">
														{t(
															`form.change_package.rules.${scope}.items.${key}.description`,
														)}
													</dd>
												</div>
											);
										},
									)}
								</dl>
							</Disclosure>
						)}
					</section>

					{/* ─── The money ────────────────────────────────────────────────── */}
					{selectedPackage && (
						<section className="space-y-3 mt-6 pt-5 border-t md:mt-0 md:pt-0 md:border-t-0 md:border-l md:pl-6">
							<div className="space-y-1">
								<FieldSetLabel>
									{t("form.change_package.payment.label")}
								</FieldSetLabel>

								{/* The heading names the decision; this names the stake — every
							    option below writes a different payment record, and none of
							    them moves money. */}
								<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
									{t("form.change_package.payment.hint")}
								</p>
							</div>

							<FormField
								control={form.control}
								name="preset"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<RadioGroup
												value={field.value}
												onValueChange={field.onChange}
												className="gap-0 rounded-md border divide-y bg-card"
											>
												{presets.map((key) => {
													const risky = RISKY_PRESETS.includes(key);
													const active = field.value === key;

													return (
														<Label
															key={key}
															htmlFor={`payment-preset-${key}`}
															className={cn(
																"flex items-start gap-3 p-3 cursor-pointer font-normal transition-colors",
																// The row is the target, so the row shows the ring —
																// a 16px dot lighting up inside a 72px row reads as
																// nothing at all to a keyboard user.
																"has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-inset",
																// Risk is carried by a hairline on the edge, not by
																// a coloured card that shouts before it is chosen.
																risky && "border-l-2 border-l-amber-500/70",
																active && "bg-muted/50",
															)}
														>
															<RadioGroupItem
																value={key}
																id={`payment-preset-${key}`}
																className="mt-0.5"
															/>
															<span className="min-w-0 space-y-0.5">
																<span className="block text-sm font-medium leading-snug">
																	{t(
																		`form.change_package.payment.presets.${key}.label`,
																	)}
																</span>
																<span className="block text-xs text-muted-foreground text-pretty">
																	{t(
																		`form.change_package.payment.presets.${key}.description`,
																	)}
																</span>
															</span>
														</Label>
													);
												})}
											</RadioGroup>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Why a cheaper package is not offering a refund. Without this the
						    missing option reads as a bug rather than as the order having no
						    money in it yet. */}
							{isRebillOnly && (
								<FormNotice
									label={t("form.change_package.payment.notice_label.rebill")}
								>
									{t("form.change_package.payment.rebill_hint")}
								</FormNotice>
							)}

							{/* Said before the submit, not after it. These presets create a
						    payment row that stays open, and nothing else on this screen says
						    so — the order list tag and the payments panel both live behind a
						    click the admin has no reason to make yet. */}
							{leavesPaymentOpen(preset) && (
								<FormNotice
									label={t(
										"form.change_package.payment.notice_label.after_save",
									)}
								>
									{t(`form.change_package.payment.follow_up.${preset}`)}
								</FormNotice>
							)}

							{preset === "gateway" && (
								<FormField
									control={form.control}
									name="expiry_minutes"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-xs">
												{t("form.change_package.payment.expiry.label")}
											</FormLabel>
											<FormControl>
												{/* Same chip row the booking form uses for this exact
											    choice, but on real buttons — the booking form's
											    version is a Badge with onClick, which no keyboard
											    can reach. */}
												<div className="flex flex-wrap gap-2">
													{EXPIRY_OPTIONS.map((value) => {
														const selected = field.value === value;

														return (
															<button
																key={value}
																type="button"
																aria-pressed={selected}
																onClick={() => field.onChange(value)}
																className={cn(
																	"rounded-md border px-3 py-1.5 text-sm transition-colors",
																	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
																	selected
																		? "border-primary bg-primary/5 text-primary font-medium ring-1 ring-primary"
																		: "text-muted-foreground shadow-inner bg-sidebar hover:bg-muted",
																)}
															>
																{t(
																	`form.change_package.payment.expiry.options.${value}`,
																)}
															</button>
														);
													})}
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}

							{(RISKY_PRESETS.includes(preset) || conflicts.length > 0) && (
								<FormField
									control={form.control}
									name="reason"
									render={({ field }) => (
										<FormItem className="pl-3 border-l-2 border-l-amber-500/70">
											<FormLabel className="text-xs">
												{t("form.change_package.payment.reason.label")}
											</FormLabel>
											<FormControl>
												<Textarea
													{...field}
													rows={2}
													placeholder={t(
														"form.change_package.payment.reason.placeholder",
													)}
													className="shadow-inner bg-sidebar text-sm"
												/>
											</FormControl>
											<p className="text-[11px] text-muted-foreground text-pretty">
												{t("form.change_package.payment.reason.hint")}
											</p>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}

							{/* The raw flags, kept honest but quiet. Read-only: every field an
						    admin may legitimately set has a preset above, and the two that
						    do not — force, reactivate_if_cancelled — are offered by the
						    backend's own refusal, next to what they would override. */}
							{/* The hint carries the "read-only" half of the old description, so an
						    admin knows it is a receipt and not a setting before opening it;
						    the rest — copy this when you report a problem — waits inside. */}
							<Disclosure
								icon={Braces}
								title={t("form.change_package.payment.advanced.header")}
								hint={t("form.change_package.payment.advanced.hint")}
							>
								<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
									{t("form.change_package.payment.advanced.description")}
								</p>
								<pre className="mt-2 rounded-md bg-muted/60 px-3 py-2 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-muted-foreground">
									{JSON.stringify(rawPayload, null, 2)}
								</pre>
							</Disclosure>
						</section>
					)}
				</div>

				{/* Pinned to the bottom of whichever container scrolls — the dialog's
				    ScrollArea on desktop, the drawer's overflow-y column on mobile. The
				    form is long enough that the submit used to sit below the fold, and
				    the negative margin lets the opaque strip reach the padded edges so
				    content passes under it rather than beside it. */}
				<div className="sticky bottom-0 -mx-1 border-t bg-background px-1 pb-1 pt-2">
					<ResponsiveDialogButton {...buttonProps} className="!mt-0" />
				</div>
			</form>

			{/* The second yes. It names the consequence in words, because nothing else
			    stops this — no role check, no audit trail. */}
			<AlertDialog
				open={!!pending}
				onOpenChange={(open) => !open && setPending(null)}
			>
				{/* Mounted only with something to ask about, so the copy is always keyed
				    by a real pending action rather than a stand-in default. */}
				{pending && (
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t(`form.change_package.confirm.${pending.kind}.title`)}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t(`form.change_package.confirm.${pending.kind}.description`)}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => submitPayload(pending.values, pending.overrides)}
							>
								{t("form.change_package.confirm.submit")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				)}
			</AlertDialog>
		</Form>
	);
}

export function RegenerateInvoiceForm({
	order,
	forceMode,
}: {
	order: {
		id: string;
		registrationNumber: string;
		status: string;
		paymentStatus: string;
	};
	forceMode?: ResponsiveDialogMode;
}) {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [newInvoiceUrl, setNewInvoiceUrl] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const { t: tappt } = useTranslation(["appointments"]);

	const buttonProps = useMemo<ResponsiveDialogButton>(
		() => ({
			isLoading,
			forceMode,
			submitText: tappt("form.regenerate_invoice.submit"),
		}),
		[isLoading, forceMode, tappt],
	);

	const formSchema = z.object({
		expiry_minutes: z.string().min(1, "Please select an expiration time"),
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			expiry_minutes: "180", // default 3 hours
		},
	});

	const EXPIRY_OPTIONS = INVOICE_EXPIRY_OPTIONS.map(({ value, key }) => ({
		value,
		label: tappt(`form.regenerate_invoice.options.${key}`),
	}));

	const handleSubmit = async (values: z.infer<typeof formSchema>) => {
		setIsLoading(true);
		setError(null);

		try {
			const csrfToken = getCsrfToken();
			const response = await fetch(
				`/api/v1/appointments/orders/${order.id}/regenerate-invoice`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": csrfToken,
					},
					body: JSON.stringify({
						form_data: {
							expiry_minutes: parseInt(values.expiry_minutes, 10),
						},
					}),
				},
			);

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(data.error || tappt("form.regenerate_invoice.failed"));
			}

			toast.success(tappt("form.regenerate_invoice.success"));
			setNewInvoiceUrl(data.data?.invoice_url || "");
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "An unexpected error occurred";
			setError(message);
			console.error("Failed to regenerate invoice:", message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCopy = useCallback(() => {
		if (!newInvoiceUrl) return;
		navigator.clipboard.writeText(newInvoiceUrl).then(() => {
			setCopied(true);
			toast.success(tappt("form.regenerate_invoice.copied"));
			setTimeout(() => setCopied(false), 2000);
		});
	}, [newInvoiceUrl, tappt]);

	if (newInvoiceUrl) {
		return (
			<div className="p-0.5 space-y-6">
				<Alert className="bg-emerald-50 border-emerald-200">
					<AlertTitle className="text-emerald-800">
						{tappt("form.regenerate_invoice.success_title")}
					</AlertTitle>
					<AlertDescription className="text-emerald-700">
						{tappt("form.regenerate_invoice.success")}
					</AlertDescription>
				</Alert>

				<div className="space-y-2">
					<Label htmlFor="invoice-url">
						{tappt("form.regenerate_invoice.new_url")}
					</Label>
					<div className="relative">
						<Input
							id="invoice-url"
							className="pe-9 truncate font-mono shadow-inner bg-sidebar select-all"
							defaultValue={newInvoiceUrl}
							readOnly
							type="text"
						/>
						<TooltipProvider delayDuration={0}>
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										aria-label={copied ? "Copied" : "Copy to clipboard"}
										className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-md text-muted-foreground/80 outline-none transition-[color,box-shadow] hover:text-foreground focus:z-10 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed"
										disabled={copied}
										onClick={handleCopy}
										type="button"
									>
										<div
											className={cn(
												"transition-all",
												copied ? "scale-100 opacity-100" : "scale-0 opacity-0",
											)}
										>
											<Check
												aria-hidden="true"
												className="stroke-emerald-500"
												size={16}
											/>
										</div>
										<div
											className={cn(
												"absolute transition-all",
												copied ? "scale-0 opacity-0" : "scale-100 opacity-100",
											)}
										>
											<Copy aria-hidden="true" size={16} />
										</div>
									</button>
								</TooltipTrigger>
								<TooltipContent className="px-2 py-1 text-xs bg-black text-white">
									Copy to clipboard
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="p-0.5 space-y-6">
			{error && (
				<Alert variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertTitle>Error</AlertTitle>
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			)}

			<Form {...form}>
				<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
					<FormField
						control={form.control}
						name="expiry_minutes"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{tappt("form.regenerate_invoice.label")}</FormLabel>
								<FormControl>
									<div className="flex flex-wrap gap-2">
										{EXPIRY_OPTIONS.map((option) => {
											const isSelected = field.value === option.value;
											return (
												<Badge
													key={option.value}
													variant={isSelected ? "default" : "outline"}
													className={cn(
														"cursor-pointer text-sm py-1.5 px-3 transition-all",
														isSelected
															? "ring-2 ring-primary ring-offset-1 shadow-sm"
															: "hover:bg-muted text-muted-foreground shadow-inner bg-sidebar",
													)}
													onClick={() => field.onChange(option.value)}
												>
													{option.label}
												</Badge>
											);
										})}
									</div>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<ResponsiveDialogButton {...buttonProps} />
				</form>
			</Form>
		</div>
	);
}

type VisitAddress = NonNullable<Appointment["visitAddress"]>;

// A numbered step of the Update Address dialog. The number carries the order; the dialog
// stays one screen, with no next buttons.
function StepNumber({ number }: { number: number }) {
	return (
		<span
			aria-hidden
			className="grid size-5 shrink-0 place-items-center border border-primary text-[11px] font-semibold tabular-nums leading-none text-primary"
		>
			{number}
		</span>
	);
}

function StepHeading({
	id,
	number,
	title,
	aside,
}: {
	id: string;
	number: number;
	title: string;
	aside?: ReactNode;
}) {
	return (
		<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
			<h3 id={id} className="flex items-center gap-2 text-sm font-semibold">
				<StepNumber number={number} />
				{title}
			</h3>
			{aside}
		</div>
	);
}

// One address, label above: the line itself, its region and postal code, and — behind
// "Show full" — the coordinates and notes.
function AddressRow({
	label,
	isTarget = false,
	address,
	meta,
	latitude,
	longitude,
	notes,
	emptyText,
	children,
}: {
	label: string;
	isTarget?: boolean;
	address?: string;
	meta: (string | null | undefined)[];
	latitude?: number | string | null;
	longitude?: number | string | null;
	notes?: string | null;
	emptyText?: string;
	children?: ReactNode;
}) {
	const { t } = useTranslation("appointments");
	const [isExpanded, setIsExpanded] = useState(false);
	const metaText = meta.filter(Boolean).join(" · ");
	const hasCoordinates = latitude != null && longitude != null;
	const canExpand =
		!!address && (address.length > 60 || hasCoordinates || !!notes);

	return (
		<div
			className={cn(
				"grid min-w-0 gap-1 px-3 py-2.5",
				isTarget ? "bg-primary/5" : "bg-muted/50",
			)}
		>
			<p
				className={cn(
					"text-xs text-muted-foreground",
					isTarget && "font-medium text-primary",
				)}
			>
				{label}
			</p>
			{address ? (
				<>
					<p
						className={cn(
							"break-words text-sm",
							isTarget && "font-semibold",
							!isExpanded && "line-clamp-2",
						)}
					>
						{address}
					</p>
					{metaText && (
						<p className="text-xs text-muted-foreground">{metaText}</p>
					)}
					{isExpanded && hasCoordinates && (
						<p className="break-all font-mono text-xs tabular-nums text-muted-foreground">
							{t("form.update_address.current_address.coordinates")}: {latitude}
							, {longitude}
						</p>
					)}
					{isExpanded && notes && (
						<p className="break-words text-xs text-muted-foreground">
							{t("form.update_address.current_address.notes")}: {notes}
						</p>
					)}
					{canExpand && (
						<button
							type="button"
							aria-expanded={isExpanded}
							onClick={() => setIsExpanded(!isExpanded)}
							className="justify-self-start rounded-sm text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-px"
						>
							{t(
								isExpanded
									? "form.update_address.expand.less"
									: "form.update_address.expand.more",
							)}
						</button>
					)}
					{children}
				</>
			) : (
				<p className="text-sm text-muted-foreground">{emptyText}</p>
			)}
		</div>
	);
}

// Who last changed a visit's address, from which source, and when.
function AddressAuditLine({ address }: { address?: VisitAddress | null }) {
	const { t } = useTranslation("appointments");
	const { locale, tzDate } = useDateContext();
	const name = address?.changedByName || "—";

	return (
		<p className="flex items-start gap-1.5 text-xs text-muted-foreground">
			<History className="mt-px size-3.5 shrink-0" />
			<span className="min-w-0">
				{!address?.changedAt ? (
					t("form.update_address.audit.never")
				) : (
					<>
						{t("form.update_address.audit.by")}{" "}
						<span className="font-medium text-foreground">{name}</span> (
						{t(`form.update_address.audit.source.${address.changeSource}`)}) ·{" "}
						{format(address.changedAt, "d MMM yyyy, p", {
							locale,
							in: tzDate,
						})}
					</>
				)}
			</span>
		</p>
	);
}

const EMPTY_ADDRESS_ENTRY: AddressEntryValues = {
	locationId: 0,
	postalCode: "",
	address: "",
	notes: "",
	coordinate: "",
};

export function UpdateAddressHistoryForm({
	selectedAppointment,
	forceMode,
	locations: locationsProp,
	pageURL: pageURLProp,
	refreshOnly: refreshOnlyProp,
}: {
	selectedAppointment: Appointment;
	forceMode?: ResponsiveDialogMode;
	locations?: Pick<Location, "id" | "city" | "state" | "country">[];
	pageURL?: string;
	refreshOnly?: string[];
}) {
	const { props: globalProps, url: pageDefaultURL } =
		usePage<AppointmentIndexGlobalPageProps>();
	const { t } = useTranslation("appointments");

	const visitAddress = selectedAppointment?.visitAddress;
	const locations = useMemo<
		Pick<Location, "id" | "city" | "state" | "country">[]
	>(() => {
		if (locationsProp) return locationsProp;
		return globalProps.filterOptionsData?.locations || [];
	}, [locationsProp, globalProps.filterOptionsData?.locations]);
	const locationName = (id?: number | null) => {
		const loc = id ? locations.find((l) => l.id === id) : undefined;
		return loc
			? [loc.city, loc.state, loc.country].filter(Boolean).join(", ")
			: null;
	};
	const cityName = (id?: number | null) =>
		locations.find((l) => l.id === id)?.city ?? "—";

	const patientActiveAddress =
		selectedAppointment.patient?.activeAddress ?? null;

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	useKeepDialogOpen(isSubmitting);

	const { locale, tzDate } = useDateContext();
	const allVisits = selectedAppointment.allVisits ?? [];
	// Locked visits stay listed, for context, but can never be chosen.
	const selectableIds = allVisits
		.filter((visit) => !isDocumentLocked(visit))
		.map((visit) => visit.id);
	const lockedCount = allVisits.length - selectableIds.length;
	const [pickedIds, setSelectedIds] = useState<string[]>(selectableIds);
	// A reload can lock a picked visit; it drops out of the selection with it.
	const selectedIds = pickedIds.filter((id) => selectableIds.includes(id));
	const [partialResult, setPartialResult] = useState<Extract<
		AddressScopeResult,
		{ mode: "subset" }
	> | null>(null);
	const isAllSelected =
		selectableIds.length > 0 &&
		selectableIds.every((id) => selectedIds.includes(id));
	// Earlier subset updates can leave a series on different addresses; the single current
	// row only holds while every visit still shares one.
	const hasOneCurrentAddress = sharesOneAddress(
		allVisits.map((visit) => visit.visitAddress),
	);
	const latestChange = allVisits
		.map((visit) => visit.visitAddress)
		.filter((address) => address?.changedAt)
		.sort((a, b) => (b?.changedAt ?? "").localeCompare(a?.changedAt ?? ""))[0];
	const toggleVisit = (id: string, checked: boolean) =>
		setSelectedIds((prev) =>
			checked ? [...prev, id] : prev.filter((selected) => selected !== id),
		);

	// ===== Address source =====
	const [source, setSource] = useState<"active" | "manual">(
		patientActiveAddress ? "active" : "manual",
	);
	const prefillVisit = pickPrefillVisit(
		allVisits,
		selectedAppointment.id,
		isDocumentLocked,
	);
	const [prefillFrom, setPrefillFrom] = useState<"visit" | "patient" | "empty">(
		prefillVisit?.visitAddress
			? "visit"
			: patientActiveAddress
				? "patient"
				: "empty",
	);

	const manualSchema = useMemo(
		() =>
			ADDRESS_ENTRY_SCHEMA.extend({
				locationId: z.coerce
					.number()
					.min(1, t("form.update_address.validation.location_required")),
				address: z
					.string()
					.trim()
					.min(1, t("form.update_address.validation.address_required"))
					.min(5, t("form.update_address.validation.address_min"))
					.max(500, t("form.update_address.validation.address_max")),
				// A pin is required; 0,0 is accepted, as on the patient address form.
				coordinate: z.string().superRefine((value, ctx) => {
					if (!value.trim()) {
						ctx.addIssue({
							code: "custom",
							message: t("form.update_address.validation.pin_required"),
						});
						return;
					}
					const parts = value.split(",").map((part) => part.trim());
					if (
						parts.length !== 2 ||
						parts.some((part) => part === "" || Number.isNaN(Number(part)))
					) {
						ctx.addIssue({
							code: "custom",
							message: t("form.update_address.validation.pin_format"),
						});
						return;
					}
					const [lat, lng] = parts.map(Number);
					if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
						ctx.addIssue({
							code: "custom",
							message: t("form.update_address.validation.pin_range"),
						});
					}
				}),
			}),
		[t],
	);
	const prefillAddress = prefillVisit?.visitAddress
		? {
				locationId: prefillVisit.visitAddress.locationId,
				postalCode: prefillVisit.visitAddress.postalCode || "",
				address: prefillVisit.visitAddress.addressLine || "",
				notes: prefillVisit.visitAddress.notes || "",
				coordinate: `${prefillVisit.visitAddress.latitude}, ${prefillVisit.visitAddress.longitude}`,
			}
		: patientActiveAddress
			? {
					locationId: patientActiveAddress.locationId,
					postalCode: patientActiveAddress.postalCode || "",
					address: patientActiveAddress.address || "",
					notes: patientActiveAddress.notes || "",
					coordinate: `${patientActiveAddress.latitude}, ${patientActiveAddress.longitude}`,
				}
			: EMPTY_ADDRESS_ENTRY;
	const form = useForm<AddressEntryValues>({
		resolver: zodResolver(manualSchema),
		defaultValues: prefillAddress,
		mode: "onChange",
	});
	const manualValues = useWatch({ control: form.control });
	const parsedManual = manualSchema.safeParse(manualValues);

	const activeTarget = patientActiveAddress && {
		addressLine: patientActiveAddress.address,
		locationId: patientActiveAddress.locationId,
		postalCode: patientActiveAddress.postalCode,
		latitude: patientActiveAddress.latitude,
		longitude: patientActiveAddress.longitude,
	};
	// The address the chosen visits would end up with; null while the form is incomplete.
	const manualPin =
		parsedManual.success && parseCoordinate(parsedManual.data.coordinate);
	const manualTarget =
		parsedManual.success && manualPin
			? {
					addressLine: parsedManual.data.address,
					locationId: parsedManual.data.locationId,
					// Rails sends a blank postal code as null, so compare it that way.
					postalCode: parsedManual.data.postalCode?.trim() || null,
					latitude: manualPin.lat,
					longitude: manualPin.lng,
					notes: parsedManual.data.notes?.trim() || null,
				}
			: null;
	const targetAddress = source === "active" ? activeTarget : manualTarget;

	const therapistWarnings = visitsToWarnAboutTherapist(
		allVisits,
		selectedIds,
		targetAddress,
	);
	const cityChanges = therapistWarnings.filter(
		(visit) => visit.visitAddress?.locationId !== targetAddress?.locationId,
	);

	const buttonProps = useMemo<ResponsiveDialogButton>(
		() => ({
			isLoading: isSubmitting,
			forceMode,
			submitText: t("form.update_address.submit"),
		}),
		[isSubmitting, forceMode, t],
	);

	const hasNoSelection = selectedIds.length === 0;
	const stepId = useId();
	const listFormat = new Intl.ListFormat(i18n.language, {
		type: "conjunction",
	});

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (hasNoSelection) return;

		let addressBody: Record<string, unknown>;
		if (source === "active") {
			if (!patientActiveAddress) return;
			addressBody = { sync_from_patient: true };
		} else {
			if (!(await form.trigger()) || !manualTarget) return;
			addressBody = {
				sync_from_patient: false,
				location_id: manualTarget.locationId,
				address_line: manualTarget.addressLine,
				postal_code: manualTarget.postalCode,
				latitude: manualTarget.latitude,
				longitude: manualTarget.longitude,
				notes: manualTarget.notes,
			};
		}

		setIsSubmitting(true);
		setSubmitError(null);
		setPartialResult(null);

		const csrfToken = getCsrfToken();
		// One id for every request this submit sends, so the change reads as one entry. Absent
		// outside a secure context, where the timeline falls back to each request's timestamp.
		const submissionId = crypto.randomUUID?.();
		const put: PutAddressHistory = async (appointmentId, applyToAllVisits) => {
			const response = await fetch(
				`/api/v1/appointments/${appointmentId}/address-history`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": csrfToken,
					},
					body: JSON.stringify({
						...addressBody,
						apply_to_all_visits: applyToAllVisits,
						submission_id: submissionId,
					}),
				},
			);
			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(
					data.message || data.error || t("form.update_address.error.submit"),
				);
			}

			return data.data?.updated_count || 1;
		};

		const refreshOnly = refreshOnlyProp || [
			"adminPortal",
			"flash",
			"errors",
			"selectedAppointment",
			"appointments",
		];

		try {
			// Locked visits are never selectable, so a series holding one always goes out
			// visit by visit instead of as a single apply_to_all_visits request.
			const result = await applyAddressToVisits({
				appointmentId: selectedAppointment.id,
				allVisits,
				selectedIds,
				put,
			});

			// A mixed result keeps the dialog open: the failed visits stay checked and marked
			// so they can be retried on their own, while the page picks up the ones that landed.
			if (result.mode === "subset" && result.failed.length) {
				setPartialResult(result);
				setSelectedIds(result.failed.map((visit) => visit.id));
				router.reload({ only: refreshOnly });
				return;
			}

			const updatedCount =
				result.mode === "all" ? result.updatedCount : result.succeeded.length;

			const pageURL = pageURLProp || pageDefaultURL;
			const { fullUrl } = populateQueryParams(pageURL, {
				update_address: null,
			});
			router.get(
				fullUrl,
				{},
				{
					only: refreshOnly,
					preserveScroll: true,
					preserveState: true,
					onSuccess: () => {
						toast.success(
							t("form.update_address.success", { count: updatedCount }),
						);
					},
				},
			);
		} catch (err: unknown) {
			setSubmitError(
				err instanceof Error
					? err.message
					: t("form.update_address.error.submit"),
			);
			router.reload({ only: refreshOnly });
		} finally {
			setIsSubmitting(false);
		}
	};

	const addressLabels = useAddressEntryLabels();

	const summaryCity =
		source === "active"
			? patientActiveAddress?.location?.city
			: manualTarget && cityName(manualTarget.locationId);
	const selectedVisitNumbers = allVisits
		.filter((visit) => selectedIds.includes(visit.id))
		.map((visit) => visit.visitNumber);

	// A step's column scrolls on its own from md up, above the always-visible summary.
	const columnClass =
		"contents md:flex md:max-h-[calc(75dvh-8rem)] md:min-w-0 md:flex-col md:gap-4 md:overflow-y-auto md:overflow-x-hidden md:overscroll-contain md:pr-2 md:[&>*]:min-w-0 md:[&>*]:shrink-0";

	return (
		// md:pr-3 keeps content clear of the desktop dialog's overlay scrollbar.
		<div className="md:pr-3">
			<Form {...form}>
				<form
					onSubmit={onSubmit}
					// Enter in the postal code or coordinate input must not send the update.
					onKeyDown={(e) => {
						if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
							e.preventDefault();
						}
					}}
					className="flex flex-col gap-4 md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:items-start md:gap-x-8"
				>
					{/* ① New address: where the visits are now, and where they go. */}
					<section
						aria-labelledby={`${stepId}-address`}
						className={columnClass}
					>
						<StepHeading
							id={`${stepId}-address`}
							number={1}
							title={t("form.update_address.steps.address")}
						/>

						{hasOneCurrentAddress ? (
							<AddressRow
								label={t("form.update_address.compare.current")}
								address={
									visitAddress ? visitAddress.addressLine || "—" : undefined
								}
								meta={[
									locationName(visitAddress?.locationId),
									visitAddress?.postalCode,
								]}
								latitude={visitAddress?.latitude}
								longitude={visitAddress?.longitude}
								notes={visitAddress?.notes}
								emptyText={t("form.update_address.current_address.empty")}
							>
								<AddressAuditLine address={latestChange} />
							</AddressRow>
						) : (
							<p className="bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground">
								{t("form.update_address.compare.differs")}
							</p>
						)}

						<fieldset disabled={isSubmitting} className="grid min-w-0 gap-2">
							<legend className="mb-2 text-xs text-muted-foreground">
								{t("form.update_address.source.label")}
							</legend>
							<RadioGroup
								value={source}
								onValueChange={(value) => setSource(value as typeof source)}
								className="flex flex-wrap gap-2"
							>
								{(
									[
										{
											value: "active",
											title: t("form.update_address.source.sync"),
											disabled: !patientActiveAddress,
										},
										{
											value: "manual",
											title: t("form.update_address.source.manual"),
											disabled: false,
										},
									] as const
								).map((option) => (
									<Label
										key={option.value}
										className={cn(
											"flex min-w-0 flex-[1_1_12rem] cursor-pointer items-center gap-2.5 border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
											source === option.value
												? "border-primary bg-primary/5 text-foreground"
												: "border-border text-muted-foreground",
											option.disabled &&
												"cursor-not-allowed opacity-60 hover:bg-transparent",
										)}
									>
										<RadioGroupItem
											value={option.value}
											disabled={option.disabled}
										/>
										{option.title}
									</Label>
								))}
							</RadioGroup>
							{!patientActiveAddress && (
								<p className="flex items-start gap-1.5 text-xs text-destructive">
									<AlertCircle className="mt-px size-3.5 shrink-0" />
									{t("form.update_address.source.no_active")}
								</p>
							)}
							<p className="text-xs text-muted-foreground">
								{t(
									source === "active"
										? "form.update_address.source.sync_description"
										: "form.update_address.source.manual_description",
								)}
							</p>
						</fieldset>

						{source === "active" && patientActiveAddress && (
							<AddressRow
								isTarget
								label={t("form.update_address.compare.target")}
								address={patientActiveAddress.address || "—"}
								meta={[
									patientActiveAddress.location &&
										[
											patientActiveAddress.location.city,
											patientActiveAddress.location.state,
											patientActiveAddress.location.country,
										]
											.filter(Boolean)
											.join(", "),
									patientActiveAddress.postalCode,
								]}
								latitude={patientActiveAddress.latitude}
								longitude={patientActiveAddress.longitude}
								notes={patientActiveAddress.notes}
							/>
						)}

						{source === "manual" && (
							<fieldset disabled={isSubmitting} className="grid min-w-0 gap-4">
								{prefillFrom !== "empty" && (
									<div className="flex items-start justify-between gap-2 text-xs text-muted-foreground">
										<p className="flex items-start gap-1.5">
											<Info className="mt-px size-3.5 shrink-0" />
											{prefillFrom === "visit"
												? t("form.update_address.prefill.from_visit", {
														number: prefillVisit?.visitNumber,
													})
												: t("form.update_address.prefill.from_patient")}
										</p>
										<button
											type="button"
											className="shrink-0 rounded-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-px disabled:opacity-50"
											onClick={() => {
												form.reset(EMPTY_ADDRESS_ENTRY);
												setPrefillFrom("empty");
											}}
										>
											{t("form.update_address.prefill.clear")}
										</button>
									</div>
								)}
								<AddressEntryFields
									form={form}
									locations={locations}
									labels={addressLabels}
									className="grid min-w-0 gap-4"
									notesClassName="min-h-20"
									mapHeight="240px"
								/>
							</fieldset>
						)}
					</section>

					{/* ② Apply to visits. */}
					<section aria-labelledby={`${stepId}-visits`} className={columnClass}>
						<fieldset disabled={isSubmitting} className="grid min-w-0 gap-3">
							<legend className="sr-only">
								{t("form.update_address.steps.visits")}
							</legend>
							{/* A long series scrolls under its heading, count and select-all, which stay put. */}
							<div className="sticky top-0 z-20 grid gap-1 border-b border-border bg-background">
								<StepHeading
									id={`${stepId}-visits`}
									number={2}
									title={t("form.update_address.steps.visits")}
									aside={
										<span
											className={cn(
												"text-xs tabular-nums text-muted-foreground",
												hasNoSelection && "text-destructive",
											)}
										>
											{t("form.update_address.visits.selected", {
												selected: selectedIds.length,
												total: selectableIds.length,
											})}
											{lockedCount > 0 &&
												t("form.update_address.visits.locked_count", {
													count: lockedCount,
												})}
										</span>
									}
								/>
								<Label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-70">
									{/* Plain checked/unchecked: the shared Checkbox draws a tick for
								    "indeterminate" too, which would read as everything selected. */}
									<Checkbox
										checked={isAllSelected}
										disabled={selectableIds.length === 0}
										onCheckedChange={(checked) =>
											setSelectedIds(checked === true ? selectableIds : [])
										}
									/>
									{t("form.update_address.visits.all")}
								</Label>
							</div>

							{/* The series as a timeline: one node per visit, in visit order. The node is the
							    checkbox, or a lock for a visit that can no longer change. */}
							<div>
								<ol className="pt-1">
									{allVisits.map((visit, index) => {
										const isLocked = isDocumentLocked(visit);
										const isSelected =
											!isLocked && selectedIds.includes(visit.id);
										const failure = partialResult?.failed.find(
											(failed) => failed.id === visit.id,
										);
										const warnsTherapist = therapistWarnings.some(
											(warned) => warned.id === visit.id,
										);
										const changesCity = cityChanges.some(
											(changed) => changed.id === visit.id,
										);
										const note = visitNoteKind({
											failed: !!failure,
											locked: isLocked,
											therapistAffected: warnsTherapist,
											alreadyTarget: isSameAddress(
												visit.visitAddress,
												targetAddress,
											),
											hasTherapist: !!visit.therapistName,
										});
										const Row = isLocked ? "div" : Label;

										return (
											<li
												key={visit.id}
												className={cn(
													"relative",
													failure && "bg-destructive/5",
												)}
											>
												{/* Rail segments above and below the node, centred on it. */}
												{index > 0 && (
													<span
														aria-hidden
														className="pointer-events-none absolute left-5 top-0 z-10 h-2.5 w-px bg-border"
													/>
												)}
												{index < allVisits.length - 1 && (
													<span
														aria-hidden
														className="pointer-events-none absolute bottom-0 left-5 top-[1.625rem] z-10 w-px bg-border"
													/>
												)}
												<Row
													className={cn(
														"relative flex items-start gap-3 px-3 py-2 text-sm font-normal",
														!isLocked &&
															"cursor-pointer transition-colors hover:bg-muted/50",
													)}
												>
													{isLocked ? (
														<span className="mt-0.5 grid size-4 shrink-0 place-items-center bg-background text-muted-foreground">
															<Lock className="size-3.5" />
														</span>
													) : (
														<Checkbox
															className="mt-0.5 bg-background"
															checked={isSelected}
															onCheckedChange={(checked) =>
																toggleVisit(visit.id, checked === true)
															}
														/>
													)}
													<span className="grid min-w-0 flex-1 gap-0.5">
														<span
															className={cn(
																"flex min-w-0 items-center gap-3",
																!isSelected && "text-muted-foreground",
															)}
														>
															<span className="min-w-0 truncate tabular-nums">
																<span
																	className={cn(
																		"font-medium",
																		isSelected && "text-foreground",
																	)}
																>
																	#{visit.visitNumber}
																</span>
																<span className="mx-1.5 text-muted-foreground">
																	·
																</span>
																{visit.appointmentDateTime
																	? format(
																			visit.appointmentDateTime,
																			"EEE d MMM",
																			{
																				locale,
																				in: tzDate,
																			},
																		)
																	: t("form.update_address.visits.unscheduled")}
															</span>
															{visit.status && (
																<DotBadgeWithLabel
																	size="xs"
																	variant={getDotVariantStatus(visit.status)}
																	className="ml-auto min-w-0"
																>
																	<span
																		className="truncate"
																		title={t(`statuses.${visit.status}`)}
																	>
																		{t(`statuses.${visit.status}`)}
																	</span>
																</DotBadgeWithLabel>
															)}
														</span>
														{!hasOneCurrentAddress && (
															<span className="grid gap-0.5 text-xs text-muted-foreground">
																{/* Always shown, in full, when the series' addresses differ: it is how the visits are told apart, and what each would move from. */}
																<span className="flex items-start gap-1.5">
																	<MapPin className="mt-px size-3 shrink-0" />
																	<span className="min-w-0 break-words">
																		{visit.visitAddress
																			? [
																					visit.visitAddress.addressLine,
																					visit.visitAddress.locationId &&
																						cityName(
																							visit.visitAddress.locationId,
																						),
																					visit.visitAddress.postalCode,
																				]
																					.filter(Boolean)
																					.join(", ")
																			: t(
																					"form.update_address.visits.no_address",
																				)}
																	</span>
																</span>
																{visit.visitAddress?.changedAt && (
																	<span className="flex items-start gap-1.5">
																		<History className="mt-px size-3 shrink-0" />
																		<span className="min-w-0">
																			{t("form.update_address.audit.compact", {
																				name:
																					visit.visitAddress.changedByName ||
																					"—",
																				date: format(
																					visit.visitAddress.changedAt,
																					"d MMM",
																					{ locale, in: tzDate },
																				),
																			})}
																		</span>
																	</span>
																)}
															</span>
														)}
														{note === "failed" && failure && (
															<span className="flex items-start gap-1.5 text-xs text-destructive">
																<AlertCircle className="mt-px size-3 shrink-0" />
																<span className="min-w-0 break-words">
																	{failure.message}
																</span>
															</span>
														)}
														{note === "locked" && (
															<span className="flex items-start gap-1.5 text-xs text-foreground">
																<Lock className="mt-px size-3 shrink-0" />
																<span className="min-w-0">
																	{t("form.update_address.visits.locked")}
																</span>
															</span>
														)}
														{note === "therapist" && (
															<span className="flex min-w-0 items-center gap-1.5 text-xs text-amber-700">
																<TriangleAlert className="size-3 shrink-0" />
																<span className="truncate">
																	{visit.therapistName}
																	{changesCity &&
																		targetAddress &&
																		` · ${cityName(visit.visitAddress?.locationId)} → ${cityName(targetAddress.locationId)}`}
																</span>
															</span>
														)}
														{note === "already" && (
															<span className="flex items-center gap-1.5 text-xs font-medium text-primary">
																<Check className="size-3 shrink-0" />
																{t("form.update_address.visits.already_target")}
															</span>
														)}
														{note === "therapistName" && (
															<span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
																<UserRound className="size-3 shrink-0" />
																<span className="truncate">
																	{visit.therapistName}
																</span>
															</span>
														)}
													</span>
												</Row>
											</li>
										);
									})}
								</ol>
							</div>
						</fieldset>
					</section>

					{/* ③ What will happen, right above the button that does it. */}
					<div className="sticky bottom-0 z-10 -mx-1 grid gap-3 border-t border-border bg-background px-1 pb-1 pt-3 md:static md:col-span-2 md:mx-0 md:px-0 md:pb-0">
						{partialResult && (
							<Alert variant="destructive">
								<AlertCircle className="size-4" />
								<AlertTitle>
									{t("form.update_address.partial.title", {
										succeeded: partialResult.succeeded.length,
										total:
											partialResult.succeeded.length +
											partialResult.failed.length,
									})}
								</AlertTitle>
								<AlertDescription>
									{t("form.update_address.partial.failed", {
										count: partialResult.failed.length,
										visits: listFormat.format(
											partialResult.failed.map(
												(visit) => `${visit.visitNumber}`,
											),
										),
									})}
								</AlertDescription>
							</Alert>
						)}

						{submitError && (
							<Alert variant="destructive">
								<AlertCircle className="size-4" />
								<AlertTitle>{t("error.title")}</AlertTitle>
								<AlertDescription>{submitError}</AlertDescription>
							</Alert>
						)}

						<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-6">
							<div className="grid min-w-0 gap-1" aria-live="polite">
								<p className="flex items-start gap-2 text-sm">
									<StepNumber number={3} />
									{!targetAddress ? (
										<span className="text-muted-foreground">
											{t("form.update_address.summary.needs_address")}
										</span>
									) : hasNoSelection ? (
										<span className="text-destructive">
											{t("form.update_address.summary.needs_visits")}
										</span>
									) : (
										<span className="line-clamp-2 min-w-0 break-words">
											<span className="font-semibold">
												{[targetAddress.addressLine, summaryCity]
													.filter(Boolean)
													.join(", ")}
											</span>
											<span className="mx-1.5 text-primary">→</span>
											{t("form.update_address.summary.visits", {
												count: selectedVisitNumbers.length,
												visits: formatVisitNumbers(selectedVisitNumbers),
											})}
										</span>
									)}
								</p>
								{therapistWarnings.length > 0 && targetAddress && (
									<p
										role="alert"
										className="flex items-start gap-1.5 pl-7 text-xs text-amber-700"
									>
										<TriangleAlert className="mt-px size-3.5 shrink-0 text-amber-600" />
										<span className="min-w-0">
											{t("form.update_address.warning.title", {
												count: therapistWarnings.length,
												visits: listFormat.format(
													therapistWarnings.map(
														(visit) => `${visit.visitNumber}`,
													),
												),
											})}
											{cityChanges.length > 0 &&
												` ${t("form.update_address.warning.city_changes")}`}
											. {t("form.update_address.warning.note")}
										</span>
									</p>
								)}
							</div>
							<ResponsiveDialogButton
								{...buttonProps}
								className="!mt-0 shrink-0 md:w-auto"
								isLoading={isSubmitting}
								disabled={
									hasNoSelection ||
									(source === "active" && !patientActiveAddress)
								}
							/>
						</div>
					</div>
				</form>
			</Form>
		</div>
	);
}

export function EditMedicalRecordForm({
	selectedAppointment,
	forceMode,
	applyToAllVisitsDefault = true,
	pageURL: pageURLProp,
	refreshOnly: refreshOnlyProp,
}: {
	selectedAppointment: Appointment;
	forceMode?: ResponsiveDialogMode;
	applyToAllVisitsDefault?: boolean;
	pageURL?: string;
	refreshOnly?: string[];
}) {
	const { url: pageDefaultURL } = usePage<AppointmentIndexGlobalPageProps>();
	const { t } = useTranslation("appointments");

	const medicalRecord = selectedAppointment?.patientMedicalRecord;

	const [complaintDescription, setComplaintDescription] = useState(
		medicalRecord?.complaintDescription || "",
	);
	const [condition, setCondition] = useState(medicalRecord?.condition || "");
	const [illnessOnsetDate, setIllnessOnsetDate] = useState(
		medicalRecord?.illnessOnsetDate || "",
	);
	const [medicalHistory, setMedicalHistory] = useState(
		medicalRecord?.medicalHistory || "",
	);
	const [applyToAllVisits, setApplyToAllVisits] = useState(
		applyToAllVisitsDefault,
	);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const buttonProps = useMemo<
		React.ComponentProps<typeof ResponsiveDialogButton>
	>(
		() => ({
			isLoading: isSubmitting,
			forceMode,
			submitText: t("form.update_medical_record.submit"),
		}),
		[isSubmitting, forceMode, t],
	);

	const conditionOptions = useMemo(() => PATIENT_CONDITIONS, []);

	const validate = useCallback((): boolean => {
		const errors: Record<string, string> = {};

		if (!complaintDescription.trim()) {
			errors.complaintDescription = t(
				"form.update_medical_record.validation.complaint_required",
			);
		}

		if (!condition) {
			errors.condition = t(
				"form.update_medical_record.validation.condition_required",
			);
		}

		setFieldErrors(errors);
		return Object.keys(errors).length === 0;
	}, [complaintDescription, condition, t]);

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validate()) return;

		setIsSubmitting(true);
		setSubmitError(null);

		try {
			const csrfToken = getCsrfToken();

			const response = await fetch(
				`/api/v1/appointments/${selectedAppointment.id}/medical-record`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": csrfToken,
					},
					body: JSON.stringify({
						apply_to_all_visits: applyToAllVisits,
						illness_onset_date: illnessOnsetDate || null,
						complaint_description: complaintDescription,
						condition,
						medical_history: medicalHistory || null,
					}),
				},
			);

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(
					data.message ||
						data.error ||
						t("form.update_medical_record.error.submit"),
				);
			}

			const updatedCount: number = data.data?.updated_count || 1;
			const pageURL = pageURLProp || pageDefaultURL;
			const { fullUrl } = populateQueryParams(pageURL, {
				edit_medical_record: null,
			});
			router.get(
				fullUrl,
				{},
				{
					only: refreshOnlyProp || [
						"adminPortal",
						"flash",
						"errors",
						"selectedAppointment",
						"appointments",
					],
					preserveScroll: true,
					preserveState: true,
					onSuccess: () => {
						toast.success(
							t("form.update_medical_record.success", { count: updatedCount }),
						);
					},
				},
			);
		} catch (err: unknown) {
			setSubmitError(
				err instanceof Error
					? err.message
					: t("form.update_medical_record.error.submit"),
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={onSubmit}>
			<div className="max-h-[80dvh] md:max-h-[70dvh] overflow-y-auto pb-6 space-y-6">
				<div className="border rounded-lg bg-card p-4">
					<p className="font-semibold uppercase text-sm text-muted-foreground/75 mb-4">
						{t("common.overview")}
					</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
						<div>
							<p className="text-muted-foreground/75 text-xs uppercase tracking-wide">
								{t("appointment.registration_number")}
							</p>
							<p className="font-medium uppercase">
								{selectedAppointment.registrationNumber || "N/A"}
							</p>
						</div>
						<div>
							<p className="text-muted-foreground/75 text-xs uppercase tracking-wide">
								{t("appointment.patient_name")}
							</p>
							<p className="font-medium text-pretty uppercase">
								{selectedAppointment.patient?.name || "N/A"}
							</p>
						</div>
						<div>
							<p className="text-muted-foreground/75 text-xs uppercase tracking-wide">
								{t("appointment.therapist")}
							</p>
							<p className="font-medium text-pretty uppercase">
								{selectedAppointment.therapist?.name || "N/A"}
							</p>
						</div>
						<div>
							<p className="text-muted-foreground/75 text-xs uppercase tracking-wide">
								{t("appointment.city")}
							</p>
							<p className="font-medium text-pretty uppercase">
								{selectedAppointment.location?.city ||
									selectedAppointment.location?.state ||
									"N/A"}
							</p>
						</div>
						<div>
							<p className="text-muted-foreground/75 text-xs uppercase tracking-wide">
								{t("appointment.service_package")}
							</p>
							<p className="font-medium text-pretty uppercase">
								{selectedAppointment.service?.name
									? `${selectedAppointment.service.name.replaceAll("_", " ")} — ${selectedAppointment.package?.name || "N/A"}`
									: "N/A"}
							</p>
						</div>
						<div>
							<p className="text-muted-foreground/75 text-xs uppercase tracking-wide">
								{t("appointment.visit")}
							</p>
							<p className="font-medium text-pretty uppercase">
								{t("appointment.visit_number", {
									current: selectedAppointment.visitNumber,
									total: selectedAppointment.totalPackageVisits,
								})}
							</p>
						</div>
					</div>
				</div>

				<div className="grid gap-6 md:gap-4">
					<div className="space-y-2">
						<Label className="capitalize">
							{t("form.update_medical_record.condition")}
						</Label>
						<RadioGroup
							value={condition}
							onValueChange={(value) => {
								setCondition(value);
								if (fieldErrors.condition) {
									setFieldErrors((prev) => {
										const next = { ...prev };
										delete next.condition;
										return next;
									});
								}
							}}
							orientation="horizontal"
							className="flex flex-col gap-3"
						>
							{conditionOptions.map((opt) => (
								<Fragment key={opt}>
									<div className="flex items-start p-4 space-x-3 space-y-0 border rounded-md bg-card shadow-inner">
										<RadioGroupItem
											value={opt}
											id={`condition-${opt}`}
											className="mt-1.5"
										/>
										<div>
											<Label
												htmlFor={`condition-${opt}`}
												className="font-normal capitalize cursor-pointer"
											>
												{t(
													`form.update_medical_record.condition_options.${opt}.label`,
												)}
											</Label>
											<p className="text-sm text-muted-foreground/80">
												{t(
													`form.update_medical_record.condition_options.${opt}.description`,
												)}
											</p>
										</div>
									</div>
								</Fragment>
							))}
						</RadioGroup>
						{fieldErrors.condition && (
							<p className="text-xs text-destructive">
								{fieldErrors.condition}
							</p>
						)}
					</div>

					<div className="space-y-2">
						<Label className="capitalize">
							{t("form.update_medical_record.complaint_description")}
						</Label>
						<Textarea
							value={complaintDescription}
							onChange={(e) => {
								setComplaintDescription(e.target.value);
								if (fieldErrors.complaintDescription) {
									setFieldErrors((prev) => {
										const next = { ...prev };
										delete next.complaintDescription;
										return next;
									});
								}
							}}
							placeholder={t(
								"form.update_medical_record.complaint_description_placeholder",
							)}
							className={cn(
								"field-sizing-content max-h-29.5 min-h-20 resize-none bg-card shadow-inner",
								fieldErrors.complaintDescription && "border-destructive",
							)}
						/>
						{fieldErrors.complaintDescription && (
							<p className="text-xs text-destructive">
								{fieldErrors.complaintDescription}
							</p>
						)}
					</div>

					<div className="space-y-2">
						<Label className="capitalize">
							{t("form.update_medical_record.illness_onset_date")}{" "}
							<span className="text-sm italic font-light">
								&mdash; (optional)
							</span>
						</Label>
						<Textarea
							value={illnessOnsetDate}
							onChange={(e) => setIllnessOnsetDate(e.target.value)}
							placeholder={t(
								"form.update_medical_record.illness_onset_date_placeholder",
							)}
							className="field-sizing-content max-h-29.5 min-h-20 resize-none bg-card shadow-inner"
						/>
						<p className="text-[0.8rem] text-muted-foreground/80">
							{t("form.update_medical_record.illness_onset_date_description")}
						</p>
					</div>

					<div className="space-y-2">
						<Label className="capitalize">
							{t("form.update_medical_record.medical_history")}{" "}
							<span className="text-sm italic font-light">
								&mdash; (optional)
							</span>
						</Label>
						<Textarea
							value={medicalHistory}
							onChange={(e) => setMedicalHistory(e.target.value)}
							placeholder={t(
								"form.update_medical_record.medical_history_placeholder",
							)}
							className="field-sizing-content max-h-29.5 min-h-20 resize-none bg-card shadow-inner"
						/>
						<p className="text-[0.8rem] text-muted-foreground/80">
							{t("form.update_medical_record.medical_history_description")}
						</p>
					</div>

					<div className="relative flex w-full items-start gap-2 rounded-md border border-input bg-card p-4 shadow-xs outline-none has-data-[state=checked]:border-primary/50">
						<Switch
							id="apply-to-all-visits"
							aria-describedby="apply-to-all-visits-description"
							checked={applyToAllVisits}
							onCheckedChange={(checked) => setApplyToAllVisits(checked)}
							className="data-[state=checked]:[&_span]:rtl:-translate-x-4 order-1 h-4 w-8 after:absolute after:inset-0 [&_span]:size-4 border border-input data-[state=checked]:[&_span]:translate-x-4 shadow"
						/>
						<div className="grid grow gap-2">
							<Label htmlFor="apply-to-all-visits">
								{t("form.update_medical_record.apply_to_all_visits")}
							</Label>
							<p
								id="apply-to-all-visits-description"
								className="text-muted-foreground/75 text-sm"
							>
								{t(
									"form.update_medical_record.apply_to_all_visits_description",
								)}
							</p>
						</div>
					</div>
				</div>

				{submitError && (
					<Alert
						variant="destructive"
						className="bg-destructive/5 animate-in fade-in slide-in-from-top-2 duration-300"
					>
						<AlertCircle className="h-4 w-4 -mt-0.5" />
						<AlertTitle className="font-bold">{t("error.title")}</AlertTitle>
						<AlertDescription>{submitError}</AlertDescription>
					</Alert>
				)}
			</div>

			<ResponsiveDialogButton {...buttonProps} />
		</form>
	);
}

// One overview fact: its name on the left, its value on the right, the way the patient
// card in the detail drawer already reads.
const OverviewRow = ({
	label,
	children,
	className,
}: {
	label: string;
	children: ReactNode;
	className?: string;
}) => (
	<div
		className={cn(
			"flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5",
			className,
		)}
	>
		<dt className="shrink-0 text-muted-foreground">{label}</dt>
		<dd className="min-w-0 grow text-right font-medium uppercase text-pretty">
			{children}
		</dd>
	</div>
);

// ─── Update SOAP ──────────────────────────────────────────────────────────────

/**
 * Corrects the SOAP notes a therapist filed.
 *
 * The therapist app and the public link are the only writers today, so a typo or a
 * mis-set final-visit block had no way back. Only what the admin actually changed is
 * sent: upstream reads a missing key as "leave this alone", which is also what keeps
 * the audit row down to the real diff.
 */
export function EditSoapForm({
	selectedAppointment,
	forceMode,
	pageURL: pageURLProp,
	refreshOnly: refreshOnlyProp,
}: {
	selectedAppointment: Appointment;
	forceMode?: ResponsiveDialogMode;
	pageURL?: string;
	refreshOnly?: string[];
}) {
	const { url: pageDefaultURL } = usePage<AppointmentIndexGlobalPageProps>();
	const { t } = useTranslation("appointments");
	const { locale, tzDate } = useDateContext();

	const soap = selectedAppointment?.soap;
	const visitOptions = selectedAppointment?.soapVisitOptions ?? [];

	// The same ceiling upstream enforces, checked here so an over-long field is caught
	// against its own label rather than coming back as one flat 400.
	const schema = useMemo(() => {
		const text = z
			.string()
			.max(
				SOAP_TEXT_LIMIT,
				t("form.edit_soap.validation.too_long", { max: SOAP_TEXT_LIMIT }),
			);

		return z.object({
			...(Object.fromEntries(
				[...SOAP_CORE_FIELDS, ...SOAP_FINAL_FIELDS].map((field) => [
					field,
					text,
				]),
			) as Record<SoapTextField, typeof text>),
			isFinalVisit: z.boolean(),
			needsFollowUp: z.boolean(),
			recommendedVisits: z
				.string()
				.regex(/^\d*$/, t("form.edit_soap.validation.digits_only")),
		});
	}, [t]);

	// Hooks run before the empty-state return below, so an absent SOAP still has to
	// produce a value object; every field reads as blank and nothing is submittable.
	const form = useForm<SoapFormValues>({
		resolver: zodResolver(schema),
		defaultValues: soapFormValues(soap ?? ({} as AppointmentSoap)),
	});
	const { isSubmitting, errors } = form.formState;

	const buttonProps = useMemo<
		React.ComponentProps<typeof ResponsiveDialogButton>
	>(
		() => ({
			isLoading: isSubmitting,
			forceMode,
			submitText: t("form.edit_soap.submit"),
		}),
		[isSubmitting, forceMode, t],
	);

	const close = useCallback(
		(onSuccess?: () => void) => {
			const { fullUrl } = populateQueryParams(pageURLProp || pageDefaultURL, {
				edit_soap: null,
			});

			router.get(
				fullUrl,
				{},
				{
					only: refreshOnlyProp || [
						"adminPortal",
						"flash",
						"errors",
						"selectedAppointment",
						"appointments",
					],
					preserveScroll: true,
					preserveState: true,
					onSuccess,
				},
			);
		},
		[pageDefaultURL, pageURLProp, refreshOnlyProp],
	);

	const onSubmit = async (values: SoapFormValues) => {
		if (!soap) return;

		const payload = buildSoapPayload(soap, values);
		// Nothing moved: saying so beats a round trip that writes nothing and an audit
		// row that records nothing.
		if (!Object.keys(payload).length) {
			close(() => toast.info(t("form.edit_soap.unchanged")));
			return;
		}

		try {
			const csrfToken = getCsrfToken();

			const response = await fetch(
				`/api/v1/appointments/${selectedAppointment.id}/soap`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": csrfToken,
					},
					body: JSON.stringify(payload),
				},
			);

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(
					data.message || data.error || t("form.edit_soap.error.submit"),
				);
			}

			// Upstream answers 200 with this attached when the visit marked final is not
			// the last one. The edit stands; the admin is the one who has to know.
			const warnings: string[] = data.warnings || [];

			close(() => {
				toast.success(t("form.edit_soap.success"));
				for (const warning of warnings) {
					// Only the mismatch has copy of its own. Anything upstream adds later is
					// shown as it comes rather than announced under the wrong wording.
					toast.warning(
						warning.startsWith("is_final_visit_mismatch")
							? t("form.edit_soap.warning.final_visit_mismatch")
							: warning,
					);
				}
			});
		} catch (err: unknown) {
			form.setError("root", {
				message:
					err instanceof Error ? err.message : t("form.edit_soap.error.submit"),
			});
		}
	};

	if (!soap) {
		return (
			<Alert variant="destructive">
				<AlertCircle className="size-4" />
				<AlertTitle>{t("form.edit_soap.empty.title")}</AlertTitle>
				<AlertDescription>
					{t("form.edit_soap.empty.description")}
				</AlertDescription>
			</Alert>
		);
	}

	// Where a field ends up once it leaves this screen — printed under another heading in
	// the final therapy report, or kept as evaluation history. Upstream decides that in
	// templates/pdf/soap_v2.txt, so the admin is told before they write rather than after
	// the PDF comes out.
	const reportNote = (field: string) => {
		const note = t(`form.edit_soap.report_note.${field}`, "");
		if (!note) return null;

		return (
			<p className="mt-0.5 flex items-start gap-2 rounded-md bg-primary/5 px-2.5 py-2 text-[0.8rem] leading-snug text-primary">
				<FileText className="mt-0.5 size-3.5 shrink-0" />
				<span>
					<Trans
						i18nKey={`form.edit_soap.report_note.${field}`}
						ns="appointments"
						components={[<b key="target" />]}
					/>
				</span>
			</p>
		);
	};

	const textField = (field: SoapTextField) => {
		// Only the fields an admin would otherwise fill in blind carry one.
		const description = t(`form.edit_soap.descriptions.${field}`, "");
		const template = t(
			`form.edit_soap.guidelines.fields.${field}.template`,
			"",
		);
		const example = t(`form.edit_soap.guidelines.fields.${field}.example`, "");
		const intro = t(`form.edit_soap.guidelines.fields.${field}.intro`, "");
		const warning = t(`form.edit_soap.guidelines.fields.${field}.warning`, "");

		return (
			<FormField
				key={field}
				control={form.control}
				name={field}
				render={({ field: controlled }) => (
					<FormItem className="space-y-2">
						{/* Label and description sit together, the box below keeps its distance:
						    the description belongs to the question, not to the answer. */}
						<div className="grid gap-1">
							<FormLabel className="leading-snug">
								{t(`form.edit_soap.fields.${field}`)}
								{field === "additionalNotes" && (
									<span className="ml-1.5 font-normal text-muted-foreground">
										&mdash; {t("form.edit_soap.optional")}
									</span>
								)}
							</FormLabel>
							{description && (
								<FormDescription className="leading-snug">
									{description}
								</FormDescription>
							)}
							{reportNote(field)}
						</div>
						<FormControl>
							<Textarea
								{...controlled}
								placeholder={t(
									`form.edit_soap.placeholders.${field}`,
									t("form.edit_soap.placeholder"),
								)}
								// Filled rather than outlined: on a white dialog an outlined
								// white field is a rectangle of nothing. Fixed height over
								// field-sizing, so two columns line up instead of laddering.
								className="min-h-28 resize-y border-transparent bg-input shadow-none transition-colors hover:bg-input/80 focus-visible:border-input focus-visible:bg-background focus-visible:ring-1"
							/>
						</FormControl>
						{template && (
							<Disclosure
								icon={Lightbulb}
								title={t("form.edit_soap.guidelines.title")}
								hint={t("form.edit_soap.guidelines.hint")}
								contentClassName="grid gap-3"
							>
								{intro && (
									<p className="text-[0.8rem] leading-relaxed text-muted-foreground">
										{intro}
									</p>
								)}
								<div className="grid gap-1.5">
									<p className="text-[0.8rem] font-semibold leading-snug">
										{t("form.edit_soap.guidelines.tips_label")}
									</p>
									<p className="border-l-2 border-primary/40 pl-3 text-[0.8rem] italic leading-snug text-muted-foreground">
										{template}
									</p>
								</div>
								{example && (
									<div className="grid gap-1.5">
										<p className="text-[0.8rem] font-semibold leading-snug">
											{t("form.edit_soap.guidelines.example_label")}
										</p>
										<p className="border-l-2 border-primary/40 pl-3 text-[0.8rem] italic leading-snug text-muted-foreground">
											{example}
										</p>
									</div>
								)}
								{warning && (
									<p className="flex items-start gap-2 rounded-md bg-amber-500/10 px-2.5 py-2 text-[0.8rem] leading-snug text-amber-700 dark:text-amber-400">
										<TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
										<span>{warning}</span>
									</p>
								)}
							</Disclosure>
						)}
						<FormMessage />
					</FormItem>
				)}
			/>
		);
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				{/* No height cap and no scroller of its own: ResponsiveDialog already scrolls
				    this content — ScrollArea on desktop, a flex-1 overflow region in the
				    drawer — and nesting a second one cut the drawer's last fields off. */}
				<div className="grid gap-6">
					<div className="grid gap-4 rounded-lg border bg-card p-4 text-sm">
						<p className="text-sm font-semibold uppercase text-muted-foreground/75">
							{t("common.overview")}
						</p>

						<dl className="grid gap-2 md:grid-cols-2 md:gap-x-8">
							<OverviewRow label={t("appointment.patient_name")}>
								{selectedAppointment.patient?.name || "N/A"}
							</OverviewRow>
							<OverviewRow label={t("list.gender")}>
								<span className="flex items-center justify-end gap-1">
									{selectedAppointment.patient?.gender &&
										getGenderIcon(
											selectedAppointment.patient.gender,
											"size-3.5 text-muted-foreground/75 shrink-0",
										)}
									{selectedAppointment.patient?.gender || "N/A"}
								</span>
							</OverviewRow>
							<OverviewRow label={t("list.date_of_birth")}>
								{selectedAppointment.patient?.dateOfBirth
									? format(
											new Date(String(selectedAppointment.patient.dateOfBirth)),
											"PP",
											{ locale, in: tzDate },
										)
									: "N/A"}
							</OverviewRow>
							<OverviewRow label={t("list.age")}>
								{selectedAppointment.patient?.age
									? `${selectedAppointment.patient.age} ${t("list.years")}`
									: "N/A"}
							</OverviewRow>
						</dl>

						<Separator />

						<dl className="grid gap-2 md:grid-cols-2 md:gap-x-8">
							<OverviewRow label={t("appointment.registration_number")}>
								{selectedAppointment.registrationNumber || "N/A"}
							</OverviewRow>
							<OverviewRow label={t("appointment.therapist")}>
								{soap.therapistName ||
									selectedAppointment.therapist?.name ||
									"N/A"}
							</OverviewRow>
							<OverviewRow label={t("appointment.visit")}>
								{t("appointment.visit_number", {
									current: selectedAppointment.visitNumber,
									total: selectedAppointment.totalPackageVisits,
								})}
							</OverviewRow>
							<OverviewRow label={t("appointment.visit_location")}>
								{selectedAppointment.location?.city ||
									selectedAppointment.location?.state ||
									"N/A"}
							</OverviewRow>
							<OverviewRow label={t("appointment.service_package")}>
								{selectedAppointment.service?.name
									? `${selectedAppointment.service.name.replaceAll("_", " ")} • ${selectedAppointment.package?.name || "N/A"}`
									: "N/A"}
								{selectedAppointment.package?.numberOfVisit
									? ` — ${selectedAppointment.package.numberOfVisit} ${t("common.visits")}`
									: ""}
							</OverviewRow>
						</dl>
					</div>

					<section className="grid gap-4 rounded-lg border bg-card p-4">
						<p className="text-sm font-semibold uppercase text-muted-foreground/75">
							{t("form.edit_soap.section")}
						</p>
						<div className="grid gap-5 md:grid-cols-2 md:gap-x-6">
							{SOAP_CORE_FIELDS.map(textField)}
						</div>
					</section>

					{/* The evaluation closes out a course of therapy, so it belongs to the last
					    visit of the package — except on a visit that already carries one, which
					    is the mis-filed evaluation an admin came here to undo. */}
					{showsTherapyEvaluation(selectedAppointment, soap) && (
						<section className="grid gap-4 rounded-lg border bg-card p-4">
							<div className="grid gap-1">
								<p className="text-sm font-semibold uppercase text-muted-foreground/75">
									{t("form.edit_soap.final_visit.title")}
								</p>
								<p className="text-[0.8rem] leading-snug text-muted-foreground">
									{t("form.edit_soap.final_visit.description")}
								</p>
							</div>

							<div className="grid gap-5 md:grid-cols-2 md:gap-x-6">
								{SOAP_FINAL_FIELDS.map(textField)}

								{/* A choice, not a toggle: it decides whether the final report carries
								    a follow-up recommendation at all, so both answers are named. */}
								<FormField
									control={form.control}
									name="needsFollowUp"
									render={({ field }) => (
										<FormItem className="space-y-2">
											<div className="grid gap-1">
												<FormLabel className="leading-snug">
													{t("form.edit_soap.fields.needsFollowUp")}
												</FormLabel>
												<FormDescription className="leading-snug">
													{t("form.edit_soap.descriptions.needsFollowUp")}
												</FormDescription>
												{reportNote("needsFollowUp")}
											</div>
											<FormControl>
												<RadioGroup
													value={field.value ? "yes" : "no"}
													onValueChange={(value) =>
														field.onChange(value === "yes")
													}
													className="grid gap-3 sm:grid-cols-2"
												>
													{(["yes", "no"] as const).map((choice) => (
														<Label
															key={choice}
															htmlFor={`soap-follow-up-${choice}`}
															className="flex cursor-pointer items-center gap-3 rounded-md border bg-muted/30 p-3 font-normal transition-colors hover:bg-muted/60 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
														>
															<RadioGroupItem
																value={choice}
																id={`soap-follow-up-${choice}`}
															/>
															{t(`form.edit_soap.follow_up.${choice}`)}
														</Label>
													))}
												</RadioGroup>
											</FormControl>
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="recommendedVisits"
									render={({ field }) => (
										<FormItem className="space-y-2">
											<div className="grid gap-1">
												<FormLabel className="leading-snug">
													{t("form.edit_soap.fields.recommendedVisits")}
												</FormLabel>
												{reportNote("recommendedVisits")}
											</div>
											{visitOptions.length ? (
												<Select
													value={field.value || undefined}
													onValueChange={field.onChange}
												>
													<FormControl>
														<SelectTrigger className="border-transparent bg-input shadow-none transition-colors hover:bg-input/80 focus:bg-background">
															<SelectValue
																placeholder={t(
																	"form.edit_soap.visit_options.placeholder",
																)}
															/>
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{visitCountOptions(visitOptions, field.value).map(
															(option) => (
																<SelectItem
																	key={option.numberOfVisit}
																	value={String(option.numberOfVisit)}
																>
																	{option.name
																		? t("form.edit_soap.visit_options.option", {
																				name: option.name,
																				count: option.numberOfVisit,
																			})
																		: t(
																				"form.edit_soap.visit_options.option_unnamed",
																				{ count: option.numberOfVisit },
																			)}
																</SelectItem>
															),
														)}
													</SelectContent>
												</Select>
											) : (
												/* No active package to name, so the count is typed instead. */
												<>
													<FormControl>
														<Input
															{...field}
															type="number"
															min={0}
															inputMode="numeric"
															className="border-transparent bg-input shadow-none transition-colors hover:bg-input/80 focus-visible:border-input focus-visible:bg-background"
														/>
													</FormControl>
													<FormDescription>
														{t("form.edit_soap.visit_options.empty")}
													</FormDescription>
												</>
											)}
											{/* An integer upstream, where a missing key means "leave it" —
											    so an emptied box keeps the stored count. */}
											<FormDescription>
												{t("form.edit_soap.fields.recommendedVisitsHint")}
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</section>
					)}

					{/* In the drawer the Cancel button belongs to the drawer's own footer and
					    never moves, so a submit that scrolls with the fields ends up out of
					    sight. Pinned to the bottom of the scroll region it sits right above
					    Cancel; on desktop it goes back to being the last row of the form. */}
					<div className="sticky bottom-0 z-10 grid gap-3 border-t border-border bg-background pb-1 pt-3">
						{errors.root && (
							<Alert variant="destructive">
								<AlertCircle className="size-4" />
								<AlertTitle>{t("error.title")}</AlertTitle>
								<AlertDescription>{errors.root.message}</AlertDescription>
							</Alert>
						)}

						<ResponsiveDialogButton
							{...buttonProps}
							className="!mt-0 shrink-0 md:w-auto"
						/>
					</div>
				</div>
			</form>
		</Form>
	);
}

// ─── Change voucher ───────────────────────────────────────────────────────────

export type { VoucherOption } from "@/lib/orders/voucher-change";

interface ChangeVoucherFormProps {
	order: NonNullable<OrdersGlobalPageProps["selectedOrder"]>;
	vouchers: VoucherOption[];
	// Told whether the change left a payment row somebody still has to close, so the
	// page can hand straight over to the settle panel.
	onSuccess?: (leavesPaymentOpen: boolean) => void;
	forceMode?: ResponsiveDialogMode;
}

// The wire shape itself, not a local restatement of it. They were two declarations of
// one thing, and `overrides` typed as `Record<string, unknown>` let a key that no
// assembly step reads ride along unnoticed — which is how the reactivate retry came to
// resend the payload that had just been refused.
type ChangeVoucherValues = VoucherFormValues;

interface PendingChangeVoucher {
	values: ChangeVoucherValues;
	overrides: Partial<ChangeVoucherValues>;
	kind: SettlementPreset | "ignore_quota" | "ignore_expiry" | "reactivate";
}

/* Hallmark · component: change-voucher dialog form · genre: modern-minimal
 * mood: utilitarian · tokens: project (shadcn/Tailwind, application.css)
 * fingerprint: what to do → which voucher → which way the money goes → confirm;
 *              hairline dividers, stacked small-caps labels, no nested cards
 * states: default · hover · focus · active · disabled · loading · error · success
 * pre-emit critique: P5 H5 E4 S5 R4 V4
 */
export function ChangeVoucherForm({
	order,
	vouchers,
	onSuccess,
	forceMode,
}: ChangeVoucherFormProps) {
	const { t } = useTranslation("appointments", { useSuspense: false });
	const { props: globalProps } = usePage<OrdersGlobalPageProps>();
	const { errors: pageErrors } = usePage().props;

	// Applying is gated by VOUCHER_ENABLE, which is off on staging and production until
	// somebody turns it on. Removing is not: the backend allows it with the flag down,
	// because undoing must always be possible.
	const canApply =
		globalProps?.adminPortal?.featureFlags?.voucherEnabled !== false;

	// Nothing to strip means REMOVE would be a no-op the backend would reject, so the
	// option is closed rather than left as a button that only ever fails.
	const hasVoucher = !!order.voucherCode;

	const submitted = pageErrors?.submitted as unknown as
		| Partial<ChangeVoucherValues>
		| undefined;

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(
		pageErrors?.message ?? null,
	);
	// Situational overrides, never presets: each appears only once something has asked
	// for it — a picked voucher that is out of stock or out of date, or a refusal that
	// named the order's cancellation.
	const [canReactivate, setCanReactivate] = useState(
		pageErrors?.code === "order_cancelled",
	);
	const [lostQuotaRace, setLostQuotaRace] = useState(
		pageErrors?.code === "quota_exhausted",
	);
	const [pending, setPending] = useState<PendingChangeVoucher | null>(null);

	const formSchema = useMemo(
		() =>
			z
				.object({
					action: z.enum(["APPLY", "REMOVE"]),
					voucher_code: z.string().optional(),
					preset: z.enum(
						Object.keys(SETTLEMENT_PRESETS) as [
							SettlementPreset,
							...SettlementPreset[],
						],
					),
					expiry_minutes: z.string().optional(),
					reason: z.string().max(500).optional(),
					ignore_quota: z.boolean().default(false),
					ignore_expiry: z.boolean().default(false),
				})
				// A code is required to apply one and meaningless to remove one, which is
				// exactly why the backend takes an explicit action instead of inferring it.
				.refine(
					(values) =>
						values.action === "REMOVE" || !!values.voucher_code?.trim(),
					{
						path: ["voucher_code"],
						message: t("form.change_voucher.validation.voucher_required"),
					},
				)
				.refine(
					(values) =>
						!RISKY_PRESETS.includes(values.preset) || !!values.reason?.trim(),
					{
						path: ["reason"],
						message: t("form.change_voucher.payment.reason.required"),
					},
				),
		[t],
	);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			// Land on whichever half is actually usable; APPLY wins when both are.
			action:
				submitted?.action ?? (canApply || !hasVoucher ? "APPLY" : "REMOVE"),
			voucher_code: submitted?.voucher_code ?? "",
			preset: submitted?.preset ?? "refund_due",
			expiry_minutes: submitted?.expiry_minutes ?? DEFAULT_EXPIRY,
			reason: submitted?.reason ?? "",
			ignore_quota: false,
			ignore_expiry: false,
		},
	});

	const action = useWatch({ control: form.control, name: "action" });
	const voucherCode = useWatch({ control: form.control, name: "voucher_code" });
	const preset = useWatch({ control: form.control, name: "preset" });
	const expiryMinutes = useWatch({
		control: form.control,
		name: "expiry_minutes",
	});
	const reason = useWatch({ control: form.control, name: "reason" });
	const ignoreQuota = useWatch({ control: form.control, name: "ignore_quota" });
	const ignoreExpiry = useWatch({
		control: form.control,
		name: "ignore_expiry",
	});

	// The quiet half of a voucher row: what it is called and what it demands. Kept out
	// of the badge line so the badges stay reserved for reasons the backend might refuse.
	// Quota is omitted once exhausted — the badge already says that, louder.
	const voucherMeta = useCallback(
		(voucher: VoucherOption) => {
			const parts: string[] = [];
			if (voucher.name && voucher.name !== voucher.code)
				parts.push(voucher.name);
			if (voucher.remainingQuota > 0) {
				parts.push(
					t("form.change_voucher.voucher.quota_left", {
						count: voucher.remainingQuota,
					}),
				);
			}
			// Omitted once the order falls short of it — the badge says so far louder, and
			// repeating the figure as neutral metadata reads like it is merely informative.
			if (voucher.minOrderAmount && !isBelowMinOrder(voucher, order.subtotal)) {
				parts.push(
					t("form.change_voucher.voucher.min_order", {
						amount: formatCurrency(voucher.minOrderAmount),
					}),
				);
			}
			if (voucher.maxDiscountAmount) {
				parts.push(
					t("form.change_voucher.voucher.max_discount", {
						amount: formatCurrency(voucher.maxDiscountAmount),
					}),
				);
			}
			return parts;
		},
		[t, order.subtotal],
	);

	// Re-picking the applied voucher is the supported way to recompute a percentage
	// discount after a package change — but only while it has something to recompute.
	// In sync, it is a row that would bill nothing and change nothing, so it is closed.
	const isNoOp = useCallback(
		(voucher: VoucherOption) =>
			isRevalidateNoOp(voucher, order.subtotal, order.discountAmount ?? 0),
		[order.subtotal, order.discountAmount],
	);

	// The one refusal with no override behind it: quota and expiry are inventory and can
	// be waived, a minimum order is pricing and never is. So this row cannot be made to
	// work from here at all, and is closed rather than left to fail at the backend.
	const belowMinOrder = useCallback(
		(voucher: VoucherOption) => isBelowMinOrder(voucher, order.subtotal),
		[order.subtotal],
	);

	const selectedVoucher = useMemo(
		() => vouchers.find((v) => v.code === voucherCode) ?? null,
		[vouchers, voucherCode],
	);

	// Which way the total moves. Removing always raises it; applying depends on whether
	// the new discount beats the one already there.
	const estimatedDelta = useMemo(
		() =>
			estimateTotalDelta({
				action,
				voucher: selectedVoucher,
				currentDiscount: order.discountAmount ?? 0,
				subtotal: order.subtotal,
			}),
		[action, selectedVoucher, order.discountAmount, order.subtotal],
	);

	// Null means "no voucher picked yet", so no direction can be shown honestly.
	const direction =
		estimatedDelta == null ? null : estimatedDelta >= 0 ? "up" : "down";
	// A falling total is not automatically a refund — see the change-package block above
	// and `presetsFor`. `estimatedDelta` is our own estimate; the backend reprices for
	// real, but it takes the refund path on the same condition this asks about.
	const presets = presetsFor({
		delta: estimatedDelta,
		settledCash: order.paidAmount,
		currentTotal: order.totalAmount,
	});

	// Said in the one case an admin would otherwise expect a refund and not find one.
	const isRebillOnly =
		estimatedDelta != null && estimatedDelta < 0 && presets === REBILL_PRESETS;

	// What the chosen voucher is blocked BY — not what the admin has decided about it.
	// A healthy voucher blocks on nothing and shows no override at all.
	const quotaBlocked =
		action === "APPLY" &&
		(lostQuotaRace ||
			(!!selectedVoucher && selectedVoucher.remainingQuota <= 0));
	const expiryBlocked =
		action === "APPLY" &&
		!!selectedVoucher &&
		!selectedVoucher.canBeUsed &&
		selectedVoucher.remainingQuota > 0;

	// Which paths make the reason mandatory rather than merely welcome. Same set the
	// submit handler gates on, named once so the field and the guard cannot drift.
	const reasonRequired =
		ignoreQuota || ignoreExpiry || RISKY_PRESETS.includes(preset);

	// An override the admin can no longer see must not still ride along. The panel is
	// gone once nothing is blocked, so that is when both ticks are dropped — clearing
	// per-flag instead would fight an admin ticking the box they can still see.
	useEffect(() => {
		if (quotaBlocked || expiryBlocked) return;
		if (form.getValues("ignore_quota")) form.setValue("ignore_quota", false);
		if (form.getValues("ignore_expiry")) form.setValue("ignore_expiry", false);
	}, [quotaBlocked, expiryBlocked, form]);

	// A preset only means something on the list it belongs to, so a change of list picks
	// its default rather than carrying an inapplicable choice over.
	// Runs even with no direction stated. The form's default is `refund_due`, and a
	// `voucher_code` restored from a failed submit can name a voucher that is no longer in
	// the list — which leaves the direction unknown while a refund preset sits armed.
	useEffect(() => {
		if (!presets.includes(form.getValues("preset"))) {
			form.setValue("preset", presets[0]);
		}
	}, [presets, form]);

	// What the preset resolves to, shown verbatim in the advanced disclosure.
	// What the admin actually ticked. Derived once and used by both the request and the
	// payload disclosure, so the receipt can never disagree with what was sent.
	const activeOverrides = useMemo(() => {
		const overrides: Partial<ChangeVoucherValues> = {};
		if (ignoreQuota) overrides.ignore_quota = true;
		if (ignoreExpiry) overrides.ignore_expiry = true;
		return overrides;
	}, [ignoreQuota, ignoreExpiry]);

	// Exactly what submit sends, not a second guess at it.
	const rawPayload = useMemo(
		() =>
			buildVoucherFormData({
				action,
				voucher_code: voucherCode,
				preset,
				expiry_minutes: expiryMinutes,
				reason,
				...activeOverrides,
			}),
		[action, voucherCode, preset, expiryMinutes, reason, activeOverrides],
	);

	const buttonProps = useMemo<ResponsiveDialogButton>(
		() => ({
			isLoading,
			forceMode,
			submitText: t("button.change_voucher.submit"),
		}),
		[isLoading, forceMode, t],
	);

	const submitPayload = (
		values: ChangeVoucherValues,
		overrides: Partial<ChangeVoucherValues> = {},
	) => {
		setIsLoading(true);
		setError(null);

		router.put(
			`/admin-portal/appointments/orders/${order.id}/change-voucher`,
			{
				form_data: buildVoucherFormData({ ...values, ...overrides }),
			},
			{
				preserveState: true,
				preserveScroll: true,
				onFinish: () => {
					setIsLoading(false);
					setPending(null);
				},
				onSuccess: () => {
					// A zero difference creates no payment row at all, so there is nothing
					// to hand over however the preset was set.
					onSuccess?.(leavesPaymentOpen(values.preset) && estimatedDelta !== 0);
				},
				onError: (errors) => {
					const defaultMessage = t("form.change_voucher.failed");
					const errorMessage = errors.message || defaultMessage;
					setError(errorMessage);
					// Re-derived from every response, never latched: an unrelated failure
					// must not leave an override armed for the next attempt.
					setCanReactivate(errors.code === "order_cancelled");
					setLostQuotaRace(errors.code === "quota_exhausted");
					console.error(defaultMessage, errorMessage);
				},
			},
		);
	};

	// Every override and every risky preset asks the same way: a reason in the form,
	// then a second yes that names the consequence out loud.
	const confirmWith = (
		kind: PendingChangeVoucher["kind"],
		overrides: Partial<ChangeVoucherValues> = {},
	) => {
		const values = form.getValues();
		if (!values.reason?.trim()) {
			form.setError("reason", {
				message: t("form.change_voucher.payment.reason.required"),
			});
			return;
		}
		setPending({ values, overrides, kind });
	};

	const handleSubmit = (values: ChangeVoucherValues) => {
		// The backend refuses a blocked voucher outright, so refuse it here with a field
		// message instead of spending a round trip on a guaranteed 400.
		if (quotaBlocked && !values.ignore_quota) {
			form.setError("ignore_quota", {
				message: t("form.change_voucher.overrides.ignore_quota.required"),
			});
			return;
		}
		if (expiryBlocked && !values.ignore_expiry) {
			form.setError("ignore_expiry", {
				message: t("form.change_voucher.overrides.ignore_expiry.required"),
			});
			return;
		}

		const overrides = activeOverrides;

		// A ticked override is the louder consequence, so it names the confirmation even
		// when a risky preset is also selected.
		if (values.ignore_quota) return confirmWith("ignore_quota", overrides);
		if (values.ignore_expiry) return confirmWith("ignore_expiry", overrides);
		if (RISKY_PRESETS.includes(values.preset)) {
			return confirmWith(values.preset, overrides);
		}

		submitPayload(values, overrides);
	};

	const retryWithReactivation = () =>
		confirmWith("reactivate", {
			reactivate_if_cancelled: true,
		});

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
				{error && (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<AlertTitle>{t("error.title")}</AlertTitle>
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				{/* Neither half of this form can do anything: applying is switched off and
				    there is no voucher to strip. Said once, at the top, instead of leaving
				    an admin to work it out from two disabled radios. */}
				{!canApply && !hasVoucher && (
					<Alert>
						<Info className="h-4 w-4" />
						<AlertDescription>
							{t("form.change_voucher.nothing_to_do")}
						</AlertDescription>
					</Alert>
				)}

				{/* Who and where, then the three facts that decide the rest of this form:
				    what discount stands now, whether the order already holds money, and
				    whether its status allows a change at all. Same shape as change-package
				    — an admin should not meet two different overviews for one order. */}
				<div className="space-y-3">
					<dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 px-1">
						<div className="min-w-0">
							<LedgerLabel>
								{t("form.change_voucher.overview.registration_number")}
							</LedgerLabel>
							<dd className="font-mono text-sm font-semibold">
								{order.registrationNumber || "—"}
							</dd>
						</div>

						<div className="min-w-0">
							<LedgerLabel>
								{t("form.change_voucher.overview.patient")}
							</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{order?.patient?.name || "—"}
							</dd>
						</div>

						<div className="min-w-0">
							<LedgerLabel>
								{t("form.change_voucher.overview.service")}
							</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{order?.appointments?.[0]?.serviceName?.replaceAll("_", " ") ||
									"—"}
							</dd>
						</div>

						<div className="min-w-0">
							<LedgerLabel>
								{t("form.change_voucher.overview.city")}
							</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{order?.appointments?.[0]?.locationCity || "—"}
							</dd>
						</div>
					</dl>

					<dl className="grid grid-cols-1 rounded-md border bg-muted/30 divide-y sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
						<div className="px-3 py-2 min-w-0">
							<LedgerLabel>
								{t("form.change_voucher.overview.current_voucher")}
							</LedgerLabel>
							<dd className="text-sm font-medium truncate">
								{order.voucherCode || t("form.change_voucher.overview.none")}
							</dd>
							<dd className="text-xs text-muted-foreground tabular-nums">
								{formatCurrency(order.discountAmount ?? 0)}
							</dd>
						</div>

						<div className="px-3 py-2 min-w-0">
							<LedgerLabel>
								{t("form.change_voucher.overview.paid")}
							</LedgerLabel>
							{/* Which settle-up branch the backend takes depends on whether
							    the order already holds cash, so the amount leads and the
							    total it is measured against sits under it. */}
							<dd className="text-sm font-medium tabular-nums">
								{formatCurrency(order.paidAmount)}
							</dd>
							<dd className="text-xs text-muted-foreground tabular-nums">
								{t("form.change_voucher.overview.of_total", {
									total: formatCurrency(order.totalAmount),
								})}
							</dd>
						</div>

						<div className="px-3 py-2 min-w-0 space-y-1">
							<LedgerLabel>
								{t("form.change_voucher.overview.status")}
							</LedgerLabel>
							<dd className="flex flex-wrap gap-1">
								<Badge
									className={cn(
										"text-[10px] border p-0 px-1 rounded-full font-bold",
										PAYMENT_STATUS_STYLES[order.paymentStatus] ||
											FALLBACK_STATUS_STYLE,
									)}
								>
									{order.paymentStatus.replace(/_/g, " ")}
								</Badge>
								<Badge
									className={cn(
										"text-[10px] border p-0 px-1 rounded-full font-bold",
										ORDER_STATUS_STYLES[order.status] || FALLBACK_STATUS_STYLE,
									)}
								>
									{order.status.replace(/_/g, " ")}
								</Badge>
							</dd>
						</div>
					</dl>
				</div>

				{/* Two columns from md up: the decision on the left, what it costs on the
				    right. Below md they stack — the money section is long enough that
				    side-by-side would squeeze both into unreadable strips. `items-start`
				    keeps the columns from stretching to match each other's height. */}
				<div className="grid gap-5 md:grid-cols-2 md:items-start md:gap-6">
					<div className="space-y-5">
						{/* ─── What to do ───────────────────────────────────────────────── */}
						<section className="space-y-3">
							<FieldSetLabel>
								{t("form.change_voucher.action.label")}
							</FieldSetLabel>

							<FormField
								control={form.control}
								name="action"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<RadioGroup
												value={field.value}
												onValueChange={field.onChange}
												className="gap-0 rounded-md border divide-y bg-card"
											>
												{(["APPLY", "REMOVE"] as const).map((key) => {
													// Two independent gates. Applying is held by the feature
													// flag; removing is held by there being something to
													// remove. They are never both open to refusal silently —
													// when both close, the alert at the top says so.
													const disabled =
														key === "APPLY" ? !canApply : !hasVoucher;

													return (
														<Label
															key={key}
															htmlFor={`voucher-action-${key}`}
															className={cn(
																"flex items-start gap-3 p-3 font-normal transition-colors",
																"has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-inset",
																disabled
																	? "cursor-not-allowed opacity-60"
																	: "cursor-pointer",
																field.value === key && "bg-muted/50",
															)}
														>
															<RadioGroupItem
																value={key}
																id={`voucher-action-${key}`}
																disabled={disabled}
																className="mt-0.5"
															/>
															<span className="min-w-0 space-y-0.5">
																<span className="block text-sm font-medium leading-snug">
																	{t(
																		`form.change_voucher.action.options.${key}.label`,
																	)}
																</span>
																<span className="block text-xs text-muted-foreground text-pretty">
																	{!disabled
																		? t(
																				`form.change_voucher.action.options.${key}.description`,
																			)
																		: key === "APPLY"
																			? t("form.change_voucher.feature_off")
																			: t(
																					"form.change_voucher.no_voucher_to_remove",
																				)}
																</span>
															</span>
														</Label>
													);
												})}
											</RadioGroup>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</section>

						{/* ─── Which voucher ────────────────────────────────────────────── */}
						{action === "APPLY" && (
							<section className="space-y-3">
								<FormField
									control={form.control}
									name="voucher_code"
									render={({ field }) => (
										<FormItem className="flex flex-col">
											<FormLabel>
												{t("form.change_voucher.voucher.label")}
											</FormLabel>
											<Popover>
												<PopoverTrigger asChild>
													<FormControl>
														{/* No role="combobox": PopoverTrigger already wires
												    aria-expanded and aria-controls onto this button, and
												    the package picker above renders a plain button for
												    the same reason. */}
														<Button
															variant="outline"
															className={cn(
																// Same surface as the package picker and the reason box: an
																// inset shadow on the sidebar tone is what marks a control as
																// something you fill in rather than a line of text.
																"w-full justify-between font-normal shadow-inner bg-sidebar",
																!field.value && "text-muted-foreground/75",
															)}
														>
															{field.value ||
																t("form.change_voucher.voucher.placeholder")}
															<ChevronsUpDown className="opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												{/* Pinned to the trigger rather than left to size itself: in the
												    two-column layout a wider popover reaches across and covers the
												    payment column behind it. */}
												<PopoverContent
													className="w-[var(--radix-popover-trigger-width)] p-0"
													align="start"
												>
													<Command>
														<CommandInput
															placeholder={t(
																"form.change_voucher.voucher.search_placeholder",
															)}
														/>
														<CommandList>
															<CommandEmpty>
																{t("form.change_voucher.voucher.empty")}
															</CommandEmpty>
															<CommandGroup>
																{vouchers.map((voucher) => (
																	<CommandItem
																		value={`${voucher.code} ${voucher.name ?? ""}`}
																		key={voucher.id}
																		disabled={
																			isNoOp(voucher) ||
																			belowMinOrder(voucher) ||
																			isPackageIneligible(voucher)
																		}
																		onSelect={() => {
																			if (
																				isNoOp(voucher) ||
																				belowMinOrder(voucher) ||
																				isPackageIneligible(voucher)
																			)
																				return;
																			form.setValue(
																				"voucher_code",
																				voucher.code,
																				{
																					shouldValidate: true,
																				},
																			);
																		}}
																		className="items-start gap-3 data-[disabled=true]:opacity-60"
																	>
																		<span className="min-w-0 flex-1 grid gap-1">
																			{/* Identity and price: the two things being
																			    compared across rows, so they hold the
																			    top line alone. */}
																			<span className="flex items-baseline justify-between gap-3">
																				<span className="font-medium tracking-tight truncate">
																					{voucher.code}
																				</span>
																				<span className="text-xs font-medium tabular-nums shrink-0">
																					{voucher.discountType ===
																					"PER_PACKAGE"
																						? typeof voucher.packageDiscount ===
																							"number"
																							? t(
																									"form.change_voucher.voucher.discount.per_package",
																									{
																										value: formatCurrency(
																											voucher.packageDiscount,
																										),
																									},
																								)
																							: t(
																									"form.change_voucher.voucher.discount.per_package_unknown",
																								)
																						: voucher.discountType ===
																								"PERCENTAGE"
																							? t(
																									"form.change_voucher.voucher.discount.percentage",
																									{
																										value:
																											voucher.discountValue,
																									},
																								)
																							: t(
																									"form.change_voucher.voucher.discount.fixed",
																									{
																										value: formatCurrency(
																											voucher.discountValue,
																										),
																									},
																								)}
																				</span>
																			</span>

																			{/* What the voucher is and what it demands,
																			    as one quiet sentence. Dot-separated
																			    rather than badged: none of it is a
																			    warning, and badging everything would
																			    leave nothing standing out. */}
																			{voucherMeta(voucher).length > 0 && (
																				<span className="text-xs text-muted-foreground leading-snug text-pretty">
																					{voucherMeta(voucher).join(" · ")}
																				</span>
																			)}

																			{/* Reasons the backend might refuse, on their
																			    own line under the description. These are
																			    the only thing here worth interrupting for,
																			    so nothing shares the line with them. */}
																			{(voucher.isCurrent ||
																				!voucher.canBeUsed ||
																				belowMinOrder(voucher) ||
																				isPackageIneligible(voucher)) && (
																				<span className="flex flex-wrap items-center gap-1">
																					{voucher.isCurrent && (
																						<Badge
																							variant="secondary"
																							className="h-4 px-1.5 text-[10px] font-normal"
																						>
																							{t(
																								"form.change_voucher.voucher.current",
																							)}
																						</Badge>
																					)}
																					{/* The applied voucher says which of the two it is: worth
									    re-picking to recompute, or already correct and
									    therefore closed above. */}
																					{voucher.isCurrent &&
																						(isNoOp(voucher) ? (
																							<span className="text-[10px] text-muted-foreground">
																								{t(
																									"form.change_voucher.voucher.in_sync",
																								)}
																							</span>
																						) : (
																							<Badge
																								variant="outline"
																								className="h-4 border-sky-500/70 px-1.5 text-[10px] font-normal text-sky-700"
																							>
																								{t(
																									"form.change_voucher.voucher.needs_recompute",
																								)}
																							</Badge>
																						))}
																					{voucher.remainingQuota <= 0 && (
																						<Badge
																							variant="outline"
																							className="h-4 border-amber-500/70 px-1.5 text-[10px] font-normal text-amber-700"
																						>
																							{t(
																								"form.change_voucher.voucher.exhausted",
																							)}
																						</Badge>
																					)}
																					{!voucher.canBeUsed &&
																						voucher.remainingQuota > 0 && (
																							<Badge
																								variant="outline"
																								className="h-4 border-amber-500/70 px-1.5 text-[10px] font-normal text-amber-700"
																							>
																								{t(
																									"form.change_voucher.voucher.expired",
																								)}
																							</Badge>
																						)}
																					{/* Destructive, not amber: the amber badges mark something an
																					    override can still get past, and this one cannot be. */}
																					{isPackageIneligible(voucher) && (
																						<Badge
																							variant="outline"
																							className="h-4 border-destructive/60 px-1.5 text-[10px] font-normal text-destructive"
																						>
																							{t(
																								"form.change_voucher.voucher.package_ineligible",
																							)}
																						</Badge>
																					)}
																					{belowMinOrder(voucher) && (
																						<Badge
																							variant="outline"
																							className="h-4 border-destructive/60 px-1.5 text-[10px] font-normal text-destructive"
																						>
																							{t(
																								"form.change_voucher.voucher.min_order_not_met",
																								{
																									amount: formatCurrency(
																										voucher.minOrderAmount ?? 0,
																									),
																								},
																							)}
																						</Badge>
																					)}
																				</span>
																			)}
																		</span>
																		{field.value === voucher.code && (
																			<Check className="mt-0.5 size-4 shrink-0" />
																		)}
																	</CommandItem>
																))}
															</CommandGroup>
														</CommandList>
													</Command>
												</PopoverContent>
											</Popover>
											<FormDescription>
												{t("form.change_voucher.voucher.revalidate_hint")}
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</section>
						)}
						{/* The mirror of the picker: on REMOVE the left column has nothing to
						    choose, so it names what is about to be dropped instead. Both figures
						    are stored facts, not projections — the discount coming off is exactly
						    what the order already records. */}
						{action === "REMOVE" && hasVoucher && (
							<section className="space-y-2">
								<FieldSetLabel>
									{t("form.change_voucher.removing.header")}
								</FieldSetLabel>
								<div className="rounded-md border bg-muted/30 px-3 py-2">
									<p className="font-medium tracking-tight truncate">
										{order.voucherCode}
									</p>
									<p className="text-xs text-muted-foreground text-pretty">
										{t("form.change_voucher.removing.detail", {
											amount: formatCurrency(order.discountAmount ?? 0),
										})}
									</p>
								</div>
							</section>
						)}
						{/* Overrides belong to the voucher, not to how the money is settled, so
						    they sit with the picker that raises them. Stacked on mobile that puts
						    them directly under the field they qualify. */}
						{(quotaBlocked || expiryBlocked) && (
							<div className="space-y-3 rounded-md border border-amber-500/40 bg-amber-50/40 p-3 dark:bg-amber-950/10">
								<FieldSetLabel>
									{t("form.change_voucher.overrides.header")}
								</FieldSetLabel>

								{/* Real boxes, not notices. These send a flag that lets a spent
								    or expired voucher through, and nothing else on this screen
								    asks the admin to own that decision.
								    Both are always shown once the panel is up: which one a
								    voucher needs is not always obvious, and hiding the other
								    made the panel look like it held a single fixed switch. */}
								{
									<FormField
										control={form.control}
										name="ignore_quota"
										render={({ field }) => (
											<FormItem className="space-y-1">
												<div className="flex items-start gap-2">
													<FormControl>
														<Checkbox
															checked={field.value}
															onCheckedChange={field.onChange}
															className="mt-0.5"
														/>
													</FormControl>
													<div className="grid gap-0.5">
														<FormLabel className="text-xs font-medium leading-snug">
															{t(
																"form.change_voucher.overrides.ignore_quota.label",
															)}
														</FormLabel>
														<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
															{lostQuotaRace
																? t("form.change_voucher.quota_race")
																: t(
																		"form.change_voucher.overrides.ignore_quota.description",
																	)}
														</p>
													</div>
												</div>
												<FormMessage />
											</FormItem>
										)}
									/>
								}

								{
									<FormField
										control={form.control}
										name="ignore_expiry"
										render={({ field }) => (
											<FormItem className="space-y-1">
												<div className="flex items-start gap-2">
													<FormControl>
														<Checkbox
															checked={field.value}
															onCheckedChange={field.onChange}
															className="mt-0.5"
														/>
													</FormControl>
													<div className="grid gap-0.5">
														<FormLabel className="text-xs font-medium leading-snug">
															{t(
																"form.change_voucher.overrides.ignore_expiry.label",
															)}
														</FormLabel>
														<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
															{t(
																"form.change_voucher.overrides.ignore_expiry.description",
															)}
														</p>
													</div>
												</div>
												<FormMessage />
											</FormItem>
										)}
									/>
								}

								<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
									{t("form.change_voucher.overrides.note")}
								</p>
							</div>
						)}

						{canReactivate && (
							<div className="space-y-2 rounded-md border border-dashed p-3">
								<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
									{t("form.change_voucher.overrides.reactivate.description")}
								</p>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={retryWithReactivation}
								>
									{t("form.change_voucher.overrides.reactivate.label")}
								</Button>
							</div>
						)}
					</div>

					{/* ─── The money ────────────────────────────────────────────────── */}
					{/* Stacked, this needs the rule above it to read as a separate step; side
				    by side it is already a separate column, and the rule would cut across
				    the grid for no reason. */}
					{/* Side by side, the payment column would otherwise be an empty half until
					    a voucher is picked. Say what will appear there instead of leaving a
					    void the eye reads as a broken layout. */}
					{action === "APPLY" && !selectedVoucher && (
						<p className="hidden md:block rounded-md border border-dashed px-3 py-2 text-[11px] leading-snug text-muted-foreground text-pretty">
							{t("form.change_voucher.direction.hint_unknown")}
						</p>
					)}

					{(action === "REMOVE" || selectedVoucher) && (
						<section className="space-y-3 mt-6 pt-5 border-t md:mt-0 md:pt-0 md:border-t-0">
							<div className="space-y-1">
								<FieldSetLabel>
									{t("form.change_voucher.payment.label")}
								</FieldSetLabel>
								<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
									{/* Zero is its own answer. A swap can land on the same discount the
									    order already carries, and telling an admin the difference will be
									    billed when there is none is worse than saying nothing. */}
									{estimatedDelta === 0
										? t("form.change_voucher.direction.hint_none")
										: direction === "down"
											? t(
													// A falling total is only a refund when the order holds
													// cash. Saying "the difference is refunded" above a list
													// with no refund on it reads as a broken dialog.
													isRebillOnly
														? "form.change_voucher.direction.hint_down_rebill"
														: "form.change_voucher.direction.hint_down",
												)
											: t("form.change_voucher.direction.hint_up")}
								</p>
							</div>

							<FormField
								control={form.control}
								name="preset"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<RadioGroup
												value={field.value}
												onValueChange={field.onChange}
												className="gap-0 rounded-md border divide-y bg-card"
											>
												{presets.map((key) => {
													const risky = RISKY_PRESETS.includes(key);

													return (
														<Label
															key={key}
															htmlFor={`voucher-preset-${key}`}
															className={cn(
																"flex items-start gap-3 p-3 cursor-pointer font-normal transition-colors",
																"has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-inset",
																risky && "border-l-2 border-l-amber-500/70",
																field.value === key && "bg-muted/50",
															)}
														>
															<RadioGroupItem
																value={key}
																id={`voucher-preset-${key}`}
																className="mt-0.5"
															/>
															<span className="min-w-0 space-y-0.5">
																<span className="block text-sm font-medium leading-snug">
																	{t(
																		`form.change_voucher.payment.presets.${key}.label`,
																	)}
																</span>
																<span className="block text-xs text-muted-foreground text-pretty">
																	{t(
																		`form.change_voucher.payment.presets.${key}.description`,
																	)}
																</span>
															</span>
														</Label>
													);
												})}
											</RadioGroup>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{leavesPaymentOpen(preset) && (
								<FormNotice
									label={t(
										"form.change_voucher.payment.notice_label.after_save",
									)}
								>
									{t(`form.change_voucher.payment.follow_up.${preset}`)}
								</FormNotice>
							)}

							{preset === "gateway" && (
								<FormField
									control={form.control}
									name="expiry_minutes"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-xs">
												{t("form.change_voucher.payment.expiry.label")}
											</FormLabel>
											<FormControl>
												{/* The same chip row change-package uses for this exact choice.
												    A select hides five short options behind a click for no gain,
												    and the two dialogs should not ask one question two ways. */}
												<div className="flex flex-wrap gap-2">
													{EXPIRY_OPTIONS.map((value) => {
														const selected = field.value === value;

														return (
															<button
																key={value}
																type="button"
																aria-pressed={selected}
																onClick={() => field.onChange(value)}
																className={cn(
																	"rounded-md border px-3 py-1.5 text-sm transition-colors",
																	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
																	selected
																		? "border-primary bg-primary/5 text-primary font-medium ring-1 ring-primary"
																		: "text-muted-foreground shadow-inner bg-sidebar hover:bg-muted",
																)}
															>
																{t(
																	`form.change_voucher.payment.expiry.options.${value}`,
																)}
															</button>
														);
													})}
												</div>
											</FormControl>
											<p className="text-[11px] text-muted-foreground text-pretty">
												{t("form.change_voucher.payment.expiry.hint")}
											</p>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}

							{/* Always offered, because this endpoint records it. The amber
							    rule appears only when something actually demands it, matching
							    how change-package marks the same field. */}
							<FormField
								control={form.control}
								name="reason"
								render={({ field }) => (
									<FormItem
										className={cn(
											reasonRequired && "pl-3 border-l-2 border-l-amber-500/70",
										)}
									>
										<FormLabel className="text-xs">
											{t("form.change_voucher.payment.reason.label")}
										</FormLabel>
										<FormControl>
											<Textarea
												{...field}
												rows={2}
												placeholder={t(
													"form.change_voucher.payment.reason.placeholder",
												)}
												className="shadow-inner bg-sidebar text-sm"
											/>
										</FormControl>
										<p className="text-[11px] text-muted-foreground text-pretty">
											{t("form.change_voucher.payment.reason.hint")}
										</p>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Read-only: every field an admin may legitimately set has a preset
						    or an override above. This is a receipt, not a setting. */}
							<Disclosure
								icon={Braces}
								title={t("form.change_voucher.payment.advanced.header")}
								hint={t("form.change_voucher.payment.advanced.hint")}
							>
								<p className="text-[11px] leading-snug text-muted-foreground text-pretty">
									{t("form.change_voucher.payment.advanced.description")}
								</p>
								<pre className="mt-2 rounded-md bg-muted/60 px-3 py-2 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-muted-foreground">
									{JSON.stringify(rawPayload, null, 2)}
								</pre>
							</Disclosure>
						</section>
					)}
				</div>
				<div className="sticky bottom-0 -mx-1 border-t bg-background px-1 pb-1 pt-2">
					<ResponsiveDialogButton {...buttonProps} className="!mt-0" />
				</div>
			</form>

			{/* The second yes. It names the consequence in words, because nothing else
			    stops this — no role check beyond the reason the admin typed. */}
			<AlertDialog
				open={!!pending}
				onOpenChange={(open) => !open && setPending(null)}
			>
				{pending && (
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t(`form.change_voucher.confirm.${pending.kind}.title`)}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t(`form.change_voucher.confirm.${pending.kind}.description`)}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => submitPayload(pending.values, pending.overrides)}
							>
								{t("form.change_voucher.confirm.submit")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				)}
			</AlertDialog>
		</Form>
	);
}
